import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { friendlyApiError } from '../utils/apiError';
import { useNavigate } from 'react-router-dom';

import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

const safeInt = (value, fallback = 0) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
};

const computeOwnTax = (subtotalCents, ratePercent) => {
  const subtotal = safeInt(subtotalCents, 0);
  const rate = Number(ratePercent);
  if (!Number.isFinite(rate) || rate <= 0) {
    return { taxCents: 0, totalCents: subtotal };
  }
  const tax = Math.max(0, subtotal) * (rate / 100);
  const taxCents = Math.ceil(tax);
  return { taxCents, totalCents: subtotal + taxCents };
};

const formatMoney = (money, fallbackCents) => {
  if (money && typeof money === 'object') {
    const amt = Number(money.amount);
    const cur = String(money.currency || '').toUpperCase();
    if (Number.isFinite(amt)) {
      if (cur === 'USD') return `$${(amt / 100).toFixed(2)}`;
      return cur ? `${amt} ${cur}` : String(amt);
    }
  }
  const cents = Number(fallbackCents);
  if (Number.isFinite(cents)) return `$${(cents / 100).toFixed(2)}`;
  return 'N/A';
};

const formatDateTimeEastern = (value) => {
  if (!value) return 'N/A';
  try {
    const n = Number(value);
    const dt = Number.isFinite(n) ? new Date(n * 1000) : new Date(value);
    if (Number.isNaN(dt.getTime())) return 'N/A';
    return dt.toLocaleString('en-US', { timeZone: 'America/New_York' });
  } catch {
    return 'N/A';
  }
};

const formatFulfillmentStatus = (raw, isDelivery = false) => {
  const s = String(raw || '').trim().toUpperCase();
  if (!s) return '';
  if (s === 'OPEN') return 'OPEN';
  
  // Delivery-specific statuses
  if (isDelivery) {
    if (s === 'READY_FOR_DELIVERY' || s === 'READY FOR DELIVERY' || s === 'READY_FOR_PICKUP' || s === 'READY FOR PICKUP' || s === 'READY' || s === 'PREPARED') return 'READY FOR DELIVERY';
    if (s === 'OUT_FOR_DELIVERY' || s === 'OUT FOR DELIVERY') return 'OUT FOR DELIVERY';
    if (s === 'DELIVERED' || s === 'DELIVERY_COMPLETE' || s === 'DELIVERY COMPLETE' || s === 'PICKUP_COMPLETE' || s === 'PICKUP COMPLETE' || s === 'PICKED UP' || s === 'PICKED_UP' || s === 'COMPLETED') return 'DELIVERED';
  } else {
    // Pickup-specific statuses
    if (s === 'READY_FOR_PICKUP' || s === 'READY FOR PICKUP' || s === 'READY' || s === 'PREPARED') return 'READY FOR PICKUP';
    if (s === 'PICKUP_COMPLETE' || s === 'PICKUP COMPLETE' || s === 'PICKED UP' || s === 'PICKED_UP' || s === 'COMPLETED') return 'PICKUP COMPLETE';
  }
  
  if (s === 'CANCELLED' || s === 'CANCELED' || s === 'CANCEL') return 'CANCELLED';
  if (s === 'PROPOSED') return 'OPEN';
  return s;
};

const escapeHtml = (s) => {
  const v = String(s ?? '');
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Compute the SLA band ('green' | 'orange' | 'red' | null) for an order.
// Only OPEN/ACTIVE orders get a band; returns null when the feature is off,
// the config is invalid, the order isn't OPEN, or the timestamp is missing.
const computeSlaBand = (sla, fulfillmentLabel, orderTimestampSeconds, nowMs = Date.now()) => {
  if (!sla || !sla.enabled) return null;
  if (String(fulfillmentLabel || '').trim().toUpperCase() !== 'OPEN') return null;

  const green = Number(sla.greenMinutes);
  const orange = Number(sla.orangeMinutes);
  const red = Number(sla.redMinutes);
  if (
    !Number.isFinite(green) || !Number.isFinite(orange) || !Number.isFinite(red) ||
    !(green > 0 && orange > 0 && red > 0) ||
    !(green < orange && orange < red)
  ) {
    return null;
  }

  const ts = Number(orderTimestampSeconds);
  if (!Number.isFinite(ts) || ts <= 0) return null;

  const elapsedMin = (nowMs / 1000 - ts) / 60;
  if (elapsedMin < green) return 'green';
  if (elapsedMin < orange) return 'orange';
  return 'red';
};

const buildPosOrderReceiptHtml = (
  order,
  {
    businessName,
    logoUrl,
    address,
    phone: businessPhone,
    website,
    orderDetailsOnly,
  } = {}
) => {
  const orderNumber = String(order?.order_number || '').trim();
  const placedAt = formatDateTimeEastern(order?.order_timestamp);
  const orderDate = (() => {
    try {
      const n = Number(order?.order_timestamp);
      const dt = Number.isFinite(n) ? new Date(n * 1000) : new Date(order?.order_timestamp);
      if (Number.isNaN(dt.getTime())) return '';
      return dt.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
    } catch {
      return '';
    }
  })();
  const orderTime = (() => {
    try {
      const n = Number(order?.order_timestamp);
      const dt = Number.isFinite(n) ? new Date(n * 1000) : new Date(order?.order_timestamp);
      if (Number.isNaN(dt.getTime())) return '';
      return dt.toLocaleTimeString('en-US', { timeZone: 'America/New_York' });
    } catch {
      return '';
    }
  })();
  const name = String(order?.customer_name || '').trim();
  const phone = String(order?.customer_phone || '').trim();
  const email = String(order?.customer_email || '').trim();
  const notes = String(order?.customerSubmittedSnapshot?.notes || '').trim();
  const items = Array.isArray(order?.customerSubmittedSnapshot?.items) ? order.customerSubmittedSnapshot.items : [];

  const safeInt = (v, fallback = 0) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    try {
      return Math.trunc(n);
    } catch {
      return fallback;
    }
  };

  // Delivery information
  const isDelivery = String(order?.fulfillment_type || '').trim() === 'delivery';
  const deliveryAddress = order?.delivery_address;
  const deliveryInstructions = String(order?.delivery_instructions || '').trim();
  const deliveryFeeCents = safeInt(order?.delivery_fee_cents, 0);

  // Otter information
  const isOtterOrder = String(order?.order_source || '').trim() === 'otter';
  const deliveryChannel = String(order?.delivery_channel || '').trim();
  const otterOrderId = String(order?.otter_order_id || '').trim();
  const requestedDeliveryTime = order?.requested_delivery_time;
  const requestedDeliveryTimeFormatted = requestedDeliveryTime ? formatDateTimeEastern(requestedDeliveryTime) : '';

  const orderSubtotalCents = safeInt(order?.subtotal_cents, 0);
  const orderTaxCents = safeInt(order?.tax_cents, 0);
  const hasStoredBreakdown = Number.isFinite(Number(order?.subtotal_cents)) && Number.isFinite(Number(order?.tax_cents));
  const subtotalCents = hasStoredBreakdown ? orderSubtotalCents : safeInt(order?.amount_total_cents, 0);
  const taxCents = hasStoredBreakdown ? orderTaxCents : 0;
  const totalCents = hasStoredBreakdown ? subtotalCents + taxCents : safeInt(order?.amount_total_cents, 0);

  const subtotalText = formatMoney(null, subtotalCents);
  const taxText = formatMoney(null, taxCents);
  const totalText = formatMoney(null, totalCents);

  const totalItems = (() => {
    let acc = 0;
    for (const it of items || []) {
      const qtyRaw = it?.quantity ?? it?.Quantity ?? 1;
      const qtyNum = Number(qtyRaw);
      const qty = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 1;
      acc += qty;
    }
    return acc;
  })();

  const effectiveBusinessName = String(businessName || '').trim() || 'YOUR RESTAURANT NAME';
  const effectiveAddress = String(address || '').trim();
  const effectiveLogoUrl = String(logoUrl || '').trim();

  const detailsOnly = Boolean(orderDetailsOnly);
  if (detailsOnly) {
    return `
      <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Order ${escapeHtml(orderNumber || '')}</title>
          <style>
            @page { size: 5.5in 8.5in; margin: 0.35in; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 0; color: #111; font-size: 15px; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 18px; }
            .cell { display: flex; gap: 8px; align-items: baseline; }
            .label { font-weight: 900; white-space: nowrap; }
            .fill { flex: 1; border-bottom: 2px solid #111; height: 0.95em; }
            .hr { border-top: 2px solid #111; margin: 16px 0; }
            .section-title { font-size: 18px; font-weight: 900; margin: 0 0 10px 0; }
            table { width: 100%; border-collapse: collapse; }
            th { text-align: left; padding: 10px 6px; font-size: 15px; border-bottom: 2px solid #111; }
            td { padding: 12px 6px; font-size: 16px; border-bottom: 1px solid #222; vertical-align: top; }
            .col-num { width: 36px; }
            .col-qty { width: 88px; text-align: right; }
            .item-notes { display: block; margin-top: 6px; font-weight: 700; }
            .write-line { border-bottom: 2px solid #111; height: 22px; margin-top: 10px; }
          </style>
        </head>
        <body>
          <div class="grid">
            <div class="cell"><span class="label">Order Number:</span><span class="fill">${escapeHtml(orderNumber || '')}</span></div>
            <div class="cell"><span class="label">Customer Name:</span><span class="fill">${escapeHtml(name || '')}</span></div>
          </div>

          ${isOtterOrder ? `
            <div class="hr"></div>
            <div style="padding: 12px; border: 3px solid #FF6B35; background: #FFF4ED;">
              <div style="font-size: 18px; font-weight: 900; margin-bottom: 8px; color: #FF6B35;">🦦 OTTER ORDER${deliveryChannel ? ` - ${escapeHtml(deliveryChannel)}` : ''}</div>
              ${otterOrderId ? `<div style="font-weight: 700; margin-top: 4px;">Otter Order ID: ${escapeHtml(otterOrderId)}</div>` : ''}
              ${requestedDeliveryTimeFormatted ? `<div style="font-weight: 700; margin-top: 4px;">Requested Delivery: ${escapeHtml(requestedDeliveryTimeFormatted)}</div>` : ''}
            </div>
          ` : ''}

          ${isDelivery && deliveryAddress ? `
            <div class="hr"></div>
            <div style="padding: 12px; border: 3px solid #111; background: #f3f4f6;">
              <div style="font-size: 18px; font-weight: 900; margin-bottom: 8px;">🚚 DELIVERY ORDER</div>
              <div style="font-weight: 800; margin-top: 6px;">
                ${escapeHtml(deliveryAddress.street)}${deliveryAddress.apt_unit ? `, ${escapeHtml(deliveryAddress.apt_unit)}` : ''}
              </div>
              <div style="font-weight: 800;">
                ${escapeHtml(deliveryAddress.city)}, ${escapeHtml(deliveryAddress.state)} ${escapeHtml(deliveryAddress.zip)}
              </div>
              ${deliveryInstructions ? `
                <div style="margin-top: 8px; font-weight: 700;">
                  <span style="font-weight: 900;">Instructions:</span> ${escapeHtml(deliveryInstructions)}
                </div>
              ` : ''}
              ${deliveryFeeCents > 0 ? `
                <div style="margin-top: 8px; font-weight: 900;">
                  Delivery Fee: ${escapeHtml(formatMoney(null, deliveryFeeCents))}
                </div>
              ` : ''}
            </div>
          ` : ''}

          <div class="hr"></div>

          <div class="section-title">ITEMIZED ORDER</div>
          <table>
            <thead>
              <tr>
                <th class="col-num">#</th>
                <th>Item Name (Include Special Instructions)</th>
                <th class="col-qty">Quantity</th>
              </tr>
            </thead>
            <tbody>
              ${(items || []).slice(0, 50).map((it, idx) => {
                const itemName = String(it?.name || it?.ProductName || 'Item').trim();
                const qtyRaw = it?.quantity ?? it?.Quantity ?? 1;
                const qtyNum = Number(qtyRaw);
                const qty = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 1;
                const itemNotes = String(it?.item_notes || it?.itemNotes || '').trim();
                return `
                  <tr>
                    <td class="col-num">${escapeHtml(idx + 1)}</td>
                    <td>
                      ${escapeHtml(itemName)}
                      ${itemNotes ? `<span class="item-notes">${escapeHtml(itemNotes)}</span>` : ''}
                    </td>
                    <td class="col-qty">${escapeHtml(qty)}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>

          <div style="margin-top: 28px;">
            <div class="section-title">ADDITIONAL NOTES / SPECIAL REQUESTS</div>
            ${notes ? `<div style="font-size:16px; font-weight:800; white-space: pre-wrap; margin-top: 10px;">${escapeHtml(notes)}</div>` : '<div class="write-line"></div>'}
          </div>
        </body>
      </html>
    `;
  }

  const isPaid = String(order?.payment_status || '').trim().toLowerCase() === 'paid';
  const checkbox = (checked) => `<span class="box${checked ? ' checked' : ''}"></span>`;

  return `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0" />
        <title>${escapeHtml(effectiveBusinessName)} - Order Form</title>
        <style>
          * { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page { 
            size: 8.5in 11in; 
            margin: 0.5in; 
          }
          @media print {
            html { 
              width: 8.5in;
              height: 11in;
            }
            body { 
              width: 7.5in !important;
              margin: 0 !important;
              padding: 0 !important;
              transform: none !important;
              -webkit-transform: none !important;
            }
          }
          html { 
            margin: 0;
            padding: 0;
          }
          body { 
            font-family: Arial, sans-serif; 
            margin: 0 auto; 
            padding: 18px; 
            color: #111; 
            font-size: 16px; 
            width: 7.5in;
            max-width: 7.5in;
            box-sizing: border-box;
          }
          .brand { font-size: 22px; font-weight: 900; margin: 0 0 10px 0; letter-spacing: 0.2px; }
          .section { margin-top: 34px; page-break-inside: avoid; }
          .section-title { font-size: 18px; font-weight: 900; margin: 0 0 10px 0; page-break-after: avoid; }
          .line-row { display: flex; gap: 10px; align-items: baseline; margin-top: 8px; }
          .label { font-weight: 800; }
          .fill { flex: 1; border-bottom: 2px solid #111; height: 0.9em; }
          .hr { border-top: 2px solid #111; margin: 28px 0; }
          table { width: 100%; border-collapse: collapse; page-break-inside: auto; }
          th { text-align: left; padding: 10px 6px; font-size: 15px; border-bottom: 2px solid #111; }
          td { padding: 12px 6px; font-size: 16px; border-bottom: 1px solid #222; vertical-align: top; page-break-inside: avoid; }
          .col-num { width: 36px; }
          .col-qty { width: 88px; text-align: right; }
          .item-notes { display: block; margin-top: 6px; font-weight: 700; }
          .muted { color: #111; font-weight: 800; }
          .box { display: inline-block; width: 14px; height: 14px; border: 2px solid #111; margin: 0 8px 0 12px; vertical-align: -2px; }
          .box.checked { background: #111; }
          .write-line { border-bottom: 2px solid #111; height: 22px; margin-top: 10px; }
          .details-grid { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 10px 18px;
            width: 100%;
          }
          .details-grid .line-row { margin-top: 0; }
          @media print {
            .details-grid {
              grid-template-columns: 1fr 1fr;
            }
          }
        </style>
      </head>
      <body>
        <div style="display:flex; align-items:flex-start; justify-content:space-between; gap:16px;">
          <div style="flex:1; min-width:0;">
            <div class="brand">${escapeHtml(effectiveBusinessName)} - Order Form</div>
          </div>
          ${effectiveLogoUrl ? `<img src="${escapeHtml(effectiveLogoUrl)}" alt="Logo" style="max-height:70px; max-width:240px; object-fit:contain;" />` : ''}
        </div>

        <div class="hr"></div>

        <div class="section">
          <div class="section-title">ORDER DETAILS</div>
          <div class="details-grid">
            <div class="line-row"><span class="label">Order Number:</span><span class="fill">${escapeHtml(orderNumber || '')}</span></div>
            <div class="line-row"><span class="label">Order Date:</span><span class="fill">${escapeHtml(orderDate || '')}</span></div>
            <div class="line-row"><span class="label">Order Time:</span><span class="fill">${escapeHtml(orderTime || '')}</span></div>
            <div class="line-row"><span class="label">Customer Name:</span><span class="fill">${escapeHtml(name || '')}</span></div>
            <div class="line-row"><span class="label">Phone Number:</span><span class="fill">${escapeHtml(phone || '')}</span></div>
            ${email ? `<div class="line-row"><span class="label">Email:</span><span class="fill">${escapeHtml(email || '')}</span></div>` : '<div></div>'}
          </div>
          ${placedAt ? `<div style="margin-top:10px;font-weight:800">Placed: ${escapeHtml(placedAt)}</div>` : ''}
          ${isOtterOrder ? `
            <div style="margin-top:18px; padding:12px; border:3px solid #FF6B35; background:#FFF4ED;">
              <div style="font-size:18px; font-weight:900; margin-bottom:8px; color:#FF6B35;">🦦 OTTER ORDER${deliveryChannel ? ` - ${escapeHtml(deliveryChannel)}` : ''}</div>
              ${otterOrderId ? `<div style="font-weight:700; margin-top:6px;">Otter Order ID: ${escapeHtml(otterOrderId)}</div>` : ''}
              ${requestedDeliveryTimeFormatted ? `<div style="font-weight:700; margin-top:6px;">Requested Delivery: ${escapeHtml(requestedDeliveryTimeFormatted)}</div>` : ''}
              <div style="margin-top:8px; font-weight:700; font-style:italic;">
                Customer ordered via ${deliveryChannel || 'third-party delivery platform'}
              </div>
            </div>
          ` : ''}
          ${isDelivery ? `
            <div style="margin-top:18px; padding:12px; border:3px solid #111; background:#f3f4f6;">
              <div style="font-size:18px; font-weight:900; margin-bottom:8px;">🚚 DELIVERY ORDER</div>
              ${deliveryAddress ? `
                <div style="font-weight:800; margin-top:6px;">
                  ${escapeHtml(deliveryAddress.street)}${deliveryAddress.apt_unit ? `, ${escapeHtml(deliveryAddress.apt_unit)}` : ''}
                </div>
                <div style="font-weight:800;">
                  ${escapeHtml(deliveryAddress.city)}, ${escapeHtml(deliveryAddress.state)} ${escapeHtml(deliveryAddress.zip)}
                </div>
              ` : ''}
              ${deliveryInstructions ? `
                <div style="margin-top:8px; font-weight:700;">
                  <span style="font-weight:900;">Instructions:</span> ${escapeHtml(deliveryInstructions)}
                </div>
              ` : ''}
              ${deliveryFeeCents > 0 ? `
                <div style="margin-top:8px; font-weight:900;">
                  Delivery Fee: ${escapeHtml(formatMoney(null, deliveryFeeCents))}
                </div>
              ` : ''}
            </div>
          ` : ''}
        </div>

        <div class="hr"></div>

        <div class="section">
          <div class="section-title">ITEMIZED ORDER</div>
          <table>
            <thead>
              <tr>
                <th class="col-num">#</th>
                <th>Item Name (Include Special Instructions)</th>
                <th class="col-qty">Quantity</th>
              </tr>
            </thead>
            <tbody>
              ${(items || []).slice(0, 50).map((it, idx) => {
                const itemName = String(it?.name || it?.ProductName || 'Item').trim();
                const qtyRaw = it?.quantity ?? it?.Quantity ?? 1;
                const qtyNum = Number(qtyRaw);
                const qty = Number.isFinite(qtyNum) && qtyNum > 0 ? qtyNum : 1;
                const itemNotes = String(it?.item_notes || it?.itemNotes || '').trim();
                return `
                  <tr>
                    <td class="col-num">${escapeHtml(idx + 1)}</td>
                    <td>
                      ${escapeHtml(itemName)}
                      ${itemNotes ? `<span class="item-notes">${escapeHtml(itemNotes)}</span>` : ''}
                    </td>
                    <td class="col-qty">${escapeHtml(qty)}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>

        <div class="section">
          <div class="section-title">ADDITIONAL NOTES / SPECIAL REQUESTS</div>
          ${notes ? `<div style="font-size:16px; font-weight:800; white-space: pre-wrap; margin-top: 10px;">${escapeHtml(notes)}</div>` : '<div class="write-line"></div>'}
        </div>

        <div class="section">
          <div class="section-title">TOTALS</div>
          <div style="width: min(380px, 100%); margin-left: auto;">
            <div class="line-row" style="justify-content:space-between;">
              <span class="label">Total items</span>
              <span style="font-weight:900;">${escapeHtml(totalItems)}</span>
            </div>
            <div class="line-row" style="justify-content:space-between;">
              <span class="label">Subtotal</span>
              <span style="font-weight:900;">${escapeHtml(subtotalText)}</span>
            </div>
            <div class="line-row" style="justify-content:space-between;">
              <span class="label">Tax</span>
              <span style="font-weight:900;">${escapeHtml(taxText)}</span>
            </div>
            <div class="line-row" style="justify-content:space-between;">
              <span class="label">Total</span>
              <span style="font-weight:900; font-size:18px;">${escapeHtml(totalText)}</span>
            </div>
          </div>

          <div style="margin-top: 18px; font-weight: 900;">
            Payment Method:${checkbox(isPaid)} Online payment${checkbox(false)} Pay at Store
          </div>
        </div>

        <div class="section">
          <div class="section-title" style="font-size:22px;">STAFF USE ONLY</div>
          <div class="line-row"><span class="label">Order Taken By:</span><span class="fill">&nbsp;</span></div>
          <div class="line-row"><span class="label">Prepared By:</span><span class="fill">&nbsp;</span></div>
          <div class="line-row"><span class="label">${isDelivery ? 'Delivery' : 'Pickup'} Time:</span><span class="fill">&nbsp;</span></div>

          <div style="margin-top: 12px; font-weight: 900;">
            Status:${checkbox(false)} Pending${checkbox(false)} In Progress${checkbox(false)} Ready${checkbox(false)} Completed
          </div>
        </div>
      </body>
    </html>
  `;
};

const printReceiptHtml = (html) => {
  if (typeof document === 'undefined') throw new Error('Printing is not available');
  const frame = document.createElement('iframe');
  frame.setAttribute('title', 'print');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.style.visibility = 'hidden';
  document.body.appendChild(frame);
  const win = frame.contentWindow;
  const doc = frame.contentDocument || win?.document;
  if (!win || !doc) {
    try {
      document.body.removeChild(frame);
    } catch {
    }
    throw new Error('Printing is not available');
  }

  doc.open();
  doc.write(String(html || ''));
  doc.close();

  const waitForImages = (d, timeoutMs = 2000) => {
    try {
      const imgs = Array.from(d?.images || []);
      const pending = imgs.filter((img) => img && !img.complete);
      if (pending.length === 0) return Promise.resolve();

      return new Promise((resolve) => {
        let settled = false;
        let remaining = pending.length;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve();
        };

        const onDone = () => {
          remaining -= 1;
          if (remaining <= 0) finish();
        };

        for (const img of pending) {
          try {
            img.addEventListener('load', onDone, { once: true });
            img.addEventListener('error', onDone, { once: true });
          } catch {
            onDone();
          }
        }

        window.setTimeout(finish, Math.max(0, Number(timeoutMs) || 0));
      });
    } catch {
      return Promise.resolve();
    }
  };

  waitForImages(doc, 2500).finally(() => {
    window.setTimeout(() => {
      try {
        win.focus();
        win.print();
      } finally {
        window.setTimeout(() => {
          try {
            document.body.removeChild(frame);
          } catch {
          }
        }, 500);
      }
    }, 50);
  });
};

const isTodayEastern = (orderTimestampSeconds) => {
  if (!orderTimestampSeconds && orderTimestampSeconds !== 0) return false;
  try {
    const n = Number(orderTimestampSeconds);
    const dt = Number.isFinite(n) ? new Date(n * 1000) : new Date(orderTimestampSeconds);
    if (Number.isNaN(dt.getTime())) return false;
    const today = new Date();

    const a = dt.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
    const b = today.toLocaleDateString('en-US', { timeZone: 'America/New_York' });
    return a === b;
  } catch {
    return false;
  }
};

const presetKeyToLabel = (key) => {
  const k = String(key || '').trim().toLowerCase();
  if (k === '1d') return '1 day (today)';
  if (k === '7d') return '7 days';
  if (k === '1m') return '1 month';
  if (k === '6m') return '6 months';
  if (k === 'ytd') return 'this year';
  if (k === 'all') return 'ALL';
  if (k === 'custom') return 'CUSTOM';
  return key ? String(key) : '7 days';
};

const presetKeyToDays = (key, customDays) => {
  const k = String(key || '').trim().toLowerCase();
  if (k === '1d') return 1;
  if (k === '7d') return 7;
  if (k === '1m') return 31;
  if (k === '6m') return 183;
  if (k === 'all') return 365;
  if (k === 'ytd') {
    try {
      const now = new Date();
      const jan1 = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
      const diffMs = now.getTime() - jan1.getTime();
      const days = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
      return Math.min(365, days);
    } catch {
      return 365;
    }
  }
  if (k === 'custom') {
    const n = Number(customDays);
    if (Number.isFinite(n) && n > 0) return Math.min(365, Math.floor(n));
    return 7;
  }
  return 7;
};

export default function AdminPosQueue() {
  const navigate = useNavigate();
  const statusRef = useRef(null);
  const autoRefreshInFlightRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [lookbackDays, setLookbackDays] = useState(7);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('any');
  const [orderStatusFilter, setOrderStatusFilter] = useState('active');
  const [salesChannelFilter, setSalesChannelFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [locations, setLocations] = useState([]);
  const [dateMode, setDateMode] = useState('preset');
  const [presetKey, setPresetKey] = useState('7d');
  const [customDays, setCustomDays] = useState('7');
  const [searchTerm, setSearchTerm] = useState('');
  const [orders, setOrders] = useState([]);
  const [newOrderUntil, setNewOrderUntil] = useState({});
  const [cursor, setCursor] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [detailsOrder, setDetailsOrder] = useState(null);

  const [posTaxEnabled, setPosTaxEnabled] = useState(false);
  const [posTaxCollectionMode, setPosTaxCollectionMode] = useState('PROVIDER');
  const [posTaxRatePercent, setPosTaxRatePercent] = useState(null);

  const [autoPrintNewOrdersEnabled, setAutoPrintNewOrdersEnabled] = useState(false);
  const [orderDetailsOnlyEnabled, setOrderDetailsOnlyEnabled] = useState(false);
  const [printBrand, setPrintBrand] = useState({ businessName: '', logoUrl: '' });
  const [slaConfig, setSlaConfig] = useState({ enabled: false, greenMinutes: 5, orangeMinutes: 10, redMinutes: 15 });
  const [slaNowMs, setSlaNowMs] = useState(() => Date.now());

  const prevQueueKeysRef = useRef(new Set());
  const hasLoadedQueueOnceRef = useRef(false);
  const autoPrintedKeysRef = useRef(new Set());
  // Newest order_timestamp (epoch seconds) observed so far. Used to tell a genuine
  // new arrival from an old order that only becomes visible after a filter change.
  const maxSeenOrderTsRef = useRef(0);

  const loadPrintingSettings = async () => {
    try {
      if (!API_BASE_URL) return;
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/general`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const autoPrintEnabled = Boolean(data?.settings?.posPrinting?.autoPrintNewOrders);
      console.log(`[AUTO-PRINT] Feature enabled: ${autoPrintEnabled}`);
      setAutoPrintNewOrdersEnabled(autoPrintEnabled);
      setOrderDetailsOnlyEnabled(Boolean(data?.settings?.posPrinting?.orderDetailsOnly));
      const sla = data?.settings?.posSla;
      if (sla && typeof sla === 'object') {
        setSlaConfig({
          enabled: Boolean(sla.enabled),
          greenMinutes: Number(sla.greenMinutes),
          orangeMinutes: Number(sla.orangeMinutes),
          redMinutes: Number(sla.redMinutes),
        });
      }
    } catch {
    }
  };

  const loadLocations = async () => {
    try {
      if (!API_BASE_URL) return;
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/locations`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const list = Array.isArray(data?.locations) ? data.locations : [];
      setLocations(list);
    } catch (e) {
      console.error('Failed to load locations:', e);
    }
  };

  // Re-tick "now" every 30s so SLA bands advance (green→orange→red) for orders
  // sitting in OPEN state, without needing a queue refetch. Only runs when the
  // SLA feature is enabled.
  useEffect(() => {
    if (!slaConfig?.enabled) return undefined;
    const id = window.setInterval(() => setSlaNowMs(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, [slaConfig?.enabled]);

  const loadPrintBranding = async () => {
    try {
      if (!API_BASE_URL) return;
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_BASE_URL}/admin/theme`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const theme = (data?.theme && typeof data.theme === 'object') ? data.theme : data;
      const nextBusinessName = String(theme?.footerBusinessName || '').trim();
      const nextLogoUrl = String(theme?.heroLogoDarkUrl || theme?.heroLogoUrl || theme?.heroLogoLightUrl || '').trim();
      setPrintBrand({ businessName: nextBusinessName, logoUrl: nextLogoUrl });
    } catch {
    }
  };

  const onPrintOrder = async (order, { silent = false } = {}) => {
    try {
      const html = buildPosOrderReceiptHtml(order, {
        businessName: printBrand?.businessName,
        logoUrl: printBrand?.logoUrl,
        orderDetailsOnly: orderDetailsOnlyEnabled,
      });
      printReceiptHtml(html);
    } catch (e) {
      if (silent) return;
      try {
        window.alert(e?.message || 'Unable to print');
      } catch {
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!API_BASE_URL) return;
        const res = await fetch(`${API_BASE_URL}/public/general`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return;

        const nextEnabled = Boolean(data?.posTaxEnabled);
        const nextModeRaw = String(data?.posTaxCollectionMode || 'PROVIDER').trim().toUpperCase();
        const nextMode = nextModeRaw === 'OWN' ? 'OWN' : 'PROVIDER';
        const rateRaw = data?.posTaxRatePercent;
        const nextRate = rateRaw === null || rateRaw === undefined ? null : Number(rateRaw);

        if (!cancelled) {
          setPosTaxEnabled(nextEnabled);
          setPosTaxCollectionMode(nextMode);
          setPosTaxRatePercent(Number.isFinite(nextRate) ? nextRate : null);
        }
      } catch {
        // ignore
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredOrders = useMemo(() => {
    const term = String(searchTerm || '').trim().toLowerCase();
    const wantStatus = String(orderStatusFilter || 'all');
    const strictToday = dateMode === 'today' || (dateMode === 'preset' && String(presetKey || '').trim().toLowerCase() === '1d');

    return (orders || []).filter((o) => {
      if (strictToday && !isTodayEastern(o?.order_timestamp)) return false;

      const orderStatus = String(o?.status || o?.order_status || '').trim().toUpperCase();
      const paymentStatus = String(o?.payment_status || o?.paymentStatus || '').trim().toLowerCase();
      const rawFulfillment = String(o?.adminFulfillmentStatus || o?.squareFulfillmentState || '').trim();
      const oIsDelivery = String(o?.fulfillment_type || '').trim().toLowerCase() === 'delivery';
      const fulfillmentLabel = formatFulfillmentStatus(rawFulfillment, oIsDelivery);
      // An order with no later fulfillment status is treated as OPEN regardless
      // of payment method (online-paid, pay-at-store) or fulfillment type, so the
      // OPEN filter and SLA stay consistent across all orders.
      const effectiveFulfillment = fulfillmentLabel || 'OPEN';
      const isFulfillmentCancelled = String(fulfillmentLabel || '').trim().toUpperCase() === 'CANCELLED';
      const isCancelled = orderStatus === 'CANCELLED' || orderStatus === 'CANCELED' || isFulfillmentCancelled;

      if (wantStatus && wantStatus !== 'all') {
        if (String(wantStatus || '').trim().toUpperCase() === 'CANCELLED') {
          if (!isCancelled) return false;
        } else {
          if (isCancelled) return false;
        }

        if (
          String(wantStatus || '').trim().toLowerCase() !== 'active' &&
          String(wantStatus || '').trim().toUpperCase() !== 'CANCELLED' &&
          effectiveFulfillment !== wantStatus
        )
          return false;
      }

      if (!term) return true;
      const orderNumber = String(o?.order_number || '').toLowerCase();
      const name = String(o?.customer_name || '').toLowerCase();
      const email = String(o?.customer_email || '').toLowerCase();
      const phone = String(o?.customer_phone || '').toLowerCase();
      return orderNumber.includes(term) || name.includes(term) || email.includes(term) || phone.includes(term);
    });
  }, [orders, searchTerm, orderStatusFilter, dateMode, presetKey]);

  const maskPhone = (raw) => {
    const s = String(raw || '').trim();
    const digits = s.replace(/\D/g, '');
    if (digits.length >= 4) return `***${digits.slice(-4)}`;
    return s;
  };

  const markAsPaid = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) {
      setError('Missing order identifiers.');
      return;
    }

    const ok = window.confirm(`Mark order ${orderNumber} as PAID?`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Marking paid…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/mark-paid`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to mark paid'));
      setStatusMessage(`Order ${orderNumber} marked PAID.`);
    } catch (e) {
      setError(e?.message || 'Failed to mark paid');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  const updateFulfillment = async (order, nextStatus) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    const orderTimestamp = order?.order_timestamp;
    if (!orderNumber || !orderTimestamp) {
      setError('Missing order identifiers.');
      return;
    }

    const target = String(nextStatus || '').trim();
    if (!target) return;

    const ok = window.confirm(`Set fulfillment for order ${orderNumber} to ${formatFulfillmentStatus(target) || target}?`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Updating fulfillment status…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/orders/fulfillment/update`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber,
          order_timestamp: orderTimestamp,
          adminFulfillmentStatus: target,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to update fulfillment status'));

      const warn = data?.squareSyncAttempted && !data?.squareSyncOk;
      setStatusMessage(warn ? 'Updated. Square not updated.' : 'Updated.');
      if (warn && data?.squareSyncError) {
        window.alert(`Square sync warning: ${String(data.squareSyncError)}`);
      }
    } catch (e) {
      setError(e?.message || 'Failed to update fulfillment status');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  const callbackUrl = useMemo(() => {
    try {
      if (!API_BASE_URL) return '';
      return `${API_BASE_URL}/admin/pos/take-payment/callback`;
    } catch {
      return '';
    }
  }, []);

  const cancelOrder = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    const orderTimestamp = order?.order_timestamp;
    if (!orderNumber || !orderTimestamp) {
      setError('Missing order identifiers.');
      return;
    }

    const posProvider = String(order?.posProvider || '').trim().toUpperCase();
    const providerLabel = posProvider === 'CLOVER' ? 'Clover' : posProvider === 'SQUARE' ? 'Square' : 'POS';
    const paymentLinkUrl = String(order?.posPaymentLink?.href || order?.posPaymentLink?.long_url || order?.posPaymentLink?.url || '').trim();
    const shouldAutoRefund = (posProvider === 'SQUARE' || posProvider === 'CLOVER') && (String(order?.posPaymentMode || '').trim().toUpperCase() === 'PAY_NOW' || !!paymentLinkUrl);
    const refundLine = shouldAutoRefund
      ? posProvider === 'SQUARE'
        ? 'If this was paid online, this will attempt an automatic Square refund.'
        : 'If this was paid online, this will attempt an automatic Clover void/refund.'
      : posProvider === 'SQUARE'
        ? 'Refund will not be automatic. If this was paid in-person, refund in Square POS.'
        : posProvider === 'CLOVER'
          ? 'Refund will not be automatic. Refund/void must be handled manually in Clover.'
          : `Refunds/cancellations in ${providerLabel} must be handled manually in ${providerLabel}.`;

    const ok = window.confirm(`Cancel order ${orderNumber}?\n\nThis will cancel the order in EzSell.\n\n${refundLine}`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Cancelling order…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/pos/orders/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber,
          order_timestamp: orderTimestamp,
          reason: '',
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to cancel order'));

      const refundMsg = data?.squareRefundId
        ? `Refund: ${String(data.squareRefundStatus || 'submitted')} (${String(data.squareRefundId)})`
        : data?.stripeRefundOk
          ? `Refund: succeeded (Stripe${data.stripeRefundId ? ' ' + String(data.stripeRefundId) : ''})`
          : data?.stripeRefundAttempted && !data?.stripeRefundOk
            ? `Refund: failed (${String(data.stripeRefundError || 'unknown')})`
        : data?.cloverRefundAttempted
          ? data?.cloverRefundOk
            ? `Refund: ${String(data.cloverRefundAction || 'submitted')} (Clover)`
            : `Refund: Clover failed (${String(data.cloverRefundError || 'unknown')})`
          : data?.cloverRefundSkipReason
            ? `Refund: skipped (${String(data.cloverRefundSkipReason)})`
          : shouldAutoRefund
            ? 'Refund: not issued (no completed online payment found)'
            : posProvider === 'SQUARE'
              ? 'Refund: manual in Square POS'
              : posProvider === 'CLOVER'
                ? 'Refund: manual in Clover'
                : `Refund: manual in ${providerLabel}`;

      const providerMsg =
        posProvider === 'SQUARE'
          ? data?.squareCancelOk
            ? 'Square: cancelled'
            : data?.squareCancelWarning
              ? `Square warning: ${String(data.squareCancelWarning)}`
              : 'Square: cancel not confirmed'
          : posProvider === 'CLOVER'
            ? data?.cloverCancelAttempted
              ? data?.cloverCancelOk
                ? 'Clover: cancelled'
                : `Clover: cancel failed (${String(data.cloverCancelError || 'unknown')})`
              : data?.cloverRefundAttempted
                ? data?.cloverRefundOk
                  ? `Clover: ${String(data.cloverRefundAction || 'refund')} submitted`
                  : `Clover: refund failed (${String(data.cloverRefundError || 'unknown')})`
                : data?.cloverRefundSkipReason
                  ? `Clover: refund skipped (${String(data.cloverRefundSkipReason)})`
                  : 'Clover: no POS action attempted'
            : posProvider === 'STRIPE'
              ? data?.stripeRefundOk
                ? 'Stripe: refund issued'
                : data?.stripeRefundAttempted
                  ? `Stripe: refund failed (${String(data.stripeRefundError || 'unknown')})`
                  : 'Stripe: order cancelled'
            : `${providerLabel}: cancel in POS not attempted`;

      window.alert(`Cancelled order ${orderNumber}.\n\n${providerMsg}\n${refundMsg}`);

      setStatusMessage(`Order ${orderNumber} cancelled.`);

      try {
        setOrders((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const next = list
            .map((it) => {
              const itNum = String(it?.order_number || '').trim();
              const itTs = it?.order_timestamp;
              if (itNum === orderNumber && String(itTs ?? '') === String(orderTimestamp ?? '')) {
                return { ...it, status: 'CANCELLED', order_status: 'CANCELLED' };
              }
              return it;
            })
            .filter((it) => {
              const want = String(orderStatusFilter || 'all').trim().toUpperCase();
              if (want === 'ALL' || want === 'CANCELLED') return true;
              const itNum = String(it?.order_number || '').trim();
              const itTs = it?.order_timestamp;
              if (itNum === orderNumber && String(itTs ?? '') === String(orderTimestamp ?? '')) return false;
              return true;
            });
          return next;
        });
      } catch {
        // ignore
      }
    } catch (e) {
      setError(e?.message || 'Failed to cancel order');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  // Uber Eats accept/reject (Requirements 12.5, 7.1-7.4). Strictly additive:
  // these only run for Uber Eats orders and call the marketplace-aware
  // admin-orders-handler routes. The backend confirms order_source=ubereats
  // and routes to the Uber Eats accept/deny endpoints.
  const acceptUberEatsOrder = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) {
      setError('Missing order identifiers.');
      return;
    }

    const ok = window.confirm(`Accept Uber Eats order ${orderNumber}?`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Accepting Uber Eats order…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to accept Uber Eats order'));

      setStatusMessage(`Uber Eats order ${orderNumber} accepted.`);
      setDetailsOrder(null);
    } catch (e) {
      setError(e?.message || 'Failed to accept Uber Eats order');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  const rejectUberEatsOrder = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) {
      setError('Missing order identifiers.');
      return;
    }

    const reason = window.prompt(
      `Reject Uber Eats order ${orderNumber}?\n\nEnter a reason (sent to Uber Eats):`,
      'Store unable to fulfill'
    );
    // window.prompt returns null when cancelled; empty string is allowed but we
    // require a non-empty reason for the deny call.
    if (reason === null) return;
    const trimmedReason = String(reason || '').trim();
    if (!trimmedReason) {
      setError('A reason is required to reject an Uber Eats order.');
      return;
    }

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Rejecting Uber Eats order…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber, reason: trimmedReason }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to reject Uber Eats order'));

      setStatusMessage(`Uber Eats order ${orderNumber} rejected.`);
      setDetailsOrder(null);
    } catch (e) {
      setError(e?.message || 'Failed to reject Uber Eats order');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  // DoorDash accept/reject (Requirements 15.6, 7.1-7.4). Strictly additive and
  // parallel to the Uber Eats handlers above: these only run for DoorDash
  // orders and call the same marketplace-aware admin-orders-handler routes. The
  // backend confirms order_source=doordash and routes to the DoorDash
  // confirm/reject endpoints.
  const acceptDoorDashOrder = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) {
      setError('Missing order identifiers.');
      return;
    }

    const ok = window.confirm(`Accept DoorDash order ${orderNumber}?`);
    if (!ok) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Accepting DoorDash order…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to accept DoorDash order'));

      setStatusMessage(`DoorDash order ${orderNumber} accepted.`);
      setDetailsOrder(null);
    } catch (e) {
      setError(e?.message || 'Failed to accept DoorDash order');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  const rejectDoorDashOrder = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (busy) return;

    const orderNumber = String(order?.order_number || '').trim();
    if (!orderNumber) {
      setError('Missing order identifiers.');
      return;
    }

    const reason = window.prompt(
      `Reject DoorDash order ${orderNumber}?\n\nEnter a reason (sent to DoorDash):`,
      'Store unable to fulfill'
    );
    // window.prompt returns null when cancelled; empty string is allowed but we
    // require a non-empty reason for the reject call.
    if (reason === null) return;
    const trimmedReason = String(reason || '').trim();
    if (!trimmedReason) {
      setError('A reason is required to reject a DoorDash order.');
      return;
    }

    try {
      setBusy(true);
      setError('');
      setStatusMessage('Rejecting DoorDash order…');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/orders/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_number: orderNumber, reason: trimmedReason }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to reject DoorDash order'));

      setStatusMessage(`DoorDash order ${orderNumber} rejected.`);
      setDetailsOrder(null);
    } catch (e) {
      setError(e?.message || 'Failed to reject DoorDash order');
    } finally {
      setBusy(false);
      try {
        await fetchQueue({ reset: true });
      } catch {
        // ignore
      }
    }
  };

  const takePayment = async (order) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }
    if (!callbackUrl) {
      setError('Missing callback URL.');
      return;
    }
    if (busy) return;

    try {
      setBusy(true);
      setError('');
      setStatusMessage('');

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const orderNumber = String(order?.order_number || '').trim();
      const name = String(order?.customer_name || '').trim();
      const phoneDigits = String(order?.customer_phone || '').replace(/\D/g, '');
      const phoneLast4 = phoneDigits.length >= 4 ? phoneDigits.slice(-4) : '';
      const noteParts = [`Order ${orderNumber}`];
      if (name) noteParts.push(name);
      if (phoneLast4) noteParts.push(`***${phoneLast4}`);
      const paymentNotes = noteParts.join(' • ');

      const res = await fetch(`${API_BASE_URL}/admin/pos/take-payment/link`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber,
          order_timestamp: order?.order_timestamp,
          callback_url: callbackUrl,
          ui_redirect: `${window.location.origin}/admin/channels/pos/queue`,
          supported_tender_types: ['CREDIT_CARD', 'CASH'],
          notes: paymentNotes,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(friendlyApiError(res.status, data, 'Failed to generate Square POS payment link'));

      const ua = String(navigator?.userAgent || '').toLowerCase();
      const isAndroid = ua.includes('android');
      const isIOS = /iphone|ipad|ipod/.test(ua) || (ua.includes('macintosh') && Number(navigator?.maxTouchPoints || 0) > 1);
      if (!isAndroid && !isIOS) {
        setError('Square POS can only be opened from an iPad/iPhone or Android device with the Square POS app installed. Open this page on the POS iPad and try again.');
        return;
      }

      const deepLinkUrl = isAndroid ? String(data?.android_url || '') : String(data?.ios_url || '');
      if (!deepLinkUrl) throw new Error('Missing deep-link URL from server');

      setStatusMessage('Opening Square POS…');
      window.location.assign(deepLinkUrl);
    } catch (e) {
      setError(e?.message || 'Failed to start Square POS payment');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!statusMessage && !error) return;
    try {
      window.setTimeout(() => {
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }, 50);
    } catch {
      // ignore
    }
  }, [statusMessage, error]);

  const hasMore = useMemo(() => Boolean(cursor), [cursor]);

  const orderKey = (order) => {
    const orderNumber = String(order?.order_number || '').trim();
    const ts = String(order?.order_timestamp || '');
    return `${orderNumber}-${ts}`;
  };

  const fetchQueue = async ({ reset, background, loadAll } = { reset: false, background: false, loadAll: false }) => {
    if (!API_BASE_URL) {
      setError('Missing API base URL (REACT_APP_ADMIN_API_URL).');
      return;
    }

    if (background) {
      if (autoRefreshInFlightRef.current) return;
      if (busy || loadingMore) return;
    } else {
      if (busy && !reset) return;
    }

    try {
      if (background) {
        autoRefreshInFlightRef.current = true;
      } else if (reset) {
        setBusy(true);
      } else {
        setLoadingMore(true);
      }

      if (!background) {
        setError('');
        setStatusMessage('');
      }

      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const shouldLoadAll = (Boolean(loadAll) || (Boolean(reset) && !background)) && !loadingMore;
      const pageLimit = 200;
      const maxPages = 10;
      const maxOrders = 2000;
      const startedAt = Date.now();
      const maxMs = 15_000;

      const fetchPage = async ({ pageCursor, includeCursor }) => {
        const url = new URL(`${API_BASE_URL}/admin/pos/orders/queue`);
        url.searchParams.set('lookback_days', String(lookbackDays));
        url.searchParams.set('payment_status', String(paymentStatusFilter || 'paid'));
        url.searchParams.set('sales_channel', String(salesChannelFilter || 'pos'));
        const orderStatusParam =
          String(orderStatusFilter || '').trim().toLowerCase() === 'all'
            ? 'all'
            : String(orderStatusFilter || '').trim().toUpperCase() === 'CANCELLED'
              ? 'cancelled'
              : 'active';
        url.searchParams.set('order_status', orderStatusParam);
        url.searchParams.set('limit', String(pageLimit));
        if (locationFilter && locationFilter !== 'all') {
          url.searchParams.set('location_id', String(locationFilter));
        }
        if (includeCursor && pageCursor) {
          url.searchParams.set('cursor', pageCursor);
        }

        const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to load POS queue');
        // The queue endpoint (authorized under "POS Queue: View") carries the SLA
        // config so POS-only users get it without "General Settings: View".
        if (data && data.posSla && typeof data.posSla === 'object') {
          setSlaConfig({
            enabled: Boolean(data.posSla.enabled),
            greenMinutes: Number(data.posSla.greenMinutes),
            orangeMinutes: Number(data.posSla.orangeMinutes),
            redMinutes: Number(data.posSla.redMinutes),
          });
        }
        return data;
      };

      let aggregated = [];
      let nextCursor = '';
      let fetchedAny = false;

      if (shouldLoadAll && reset) {
        setOrders([]);
        setCursor('');
        setNewOrderUntil({});
        if (!background) {
          setStatusMessage('Loading…');
        }

        for (let page = 0; page < maxPages; page += 1) {
          if (Date.now() - startedAt > maxMs) break;
          if (aggregated.length >= maxOrders) break;

          const data = await fetchPage({ pageCursor: nextCursor, includeCursor: page > 0 });
          const list = Array.isArray(data?.orders) ? data.orders : [];
          fetchedAny = true;

          aggregated = [...aggregated, ...list];
          nextCursor = String(data?.next_cursor || '');
          if (!background) {
            setStatusMessage(`Loaded ${aggregated.length} order(s)…`);
          }

          if (!nextCursor || list.length === 0) break;
        }
      } else {
        const includeCursor = !reset && cursor;
        const data = await fetchPage({ pageCursor: cursor, includeCursor });
        const list = Array.isArray(data?.orders) ? data.orders : [];
        fetchedAny = true;
        aggregated = reset ? list : [...(orders || []), ...list];
        nextCursor = String(data?.next_cursor || '');
      }

      const list = fetchedAny ? aggregated : [];
      const nextOrders = list;

      try {
        setNewOrderUntil((prev) => {
          const now = Date.now();
          const out = {};
          for (const [k, until] of Object.entries(prev || {})) {
            if (Number(until) > now) out[k] = until;
          }
          return out;
        });
      } catch {
        // ignore
      }

      try {
        if (reset) {
          const nextKeys = new Set(nextOrders.map(orderKey));
          const prevKeys = prevQueueKeysRef.current || new Set();

          if (hasLoadedQueueOnceRef.current) {
            const newKeys = [];
            for (const k of nextKeys) {
              if (!prevKeys.has(k)) newKeys.push(k);
            }
            if (newKeys.length) {
              // Auto-print a genuinely NEW arrival: an order whose timestamp is
              // newer than the newest order we'd already seen. This is independent
              // of the fetch path (background poll or user-initiated refresh) and
              // does NOT fire for old orders that merely become visible after a
              // filter change / status change (their timestamp is not newer than
              // the baseline), which was the source of the spurious print dialogs.
              const prevMaxTs = Number(maxSeenOrderTsRef.current) || 0;
              if (autoPrintNewOrdersEnabled) {
                try {
                  const toPrint = [];
                  for (const k of newKeys) {
                    if (autoPrintedKeysRef.current?.has?.(k)) continue;
                    const o = nextOrders.find((x) => orderKey(x) === k);
                    if (!o) continue;
                    const ts = Number(o?.order_timestamp) || 0;
                    if (ts <= prevMaxTs) continue; // old order surfaced (filter/status change) — not a new arrival
                    autoPrintedKeysRef.current.add(k);
                    toPrint.push(o);
                  }
                  if (toPrint.length) {
                    console.log(`[AUTO-PRINT] Printing ${toPrint.length} new order(s)`);
                    window.setTimeout(() => {
                      try {
                        for (const o of toPrint) {
                          const orderNum = String(o?.order_number || '').trim();
                          console.log(`[AUTO-PRINT] Printing order: ${orderNum}`);
                          onPrintOrder(o, { silent: true });
                        }
                      } catch (err) {
                        console.error('[AUTO-PRINT] Error printing orders:', err);
                      }
                    }, 50);
                  } else {
                    console.log('[AUTO-PRINT] No genuinely new orders to print');
                  }
                } catch (err) {
                  console.error('[AUTO-PRINT] Error in auto-print logic:', err);
                }
              } else {
                console.log('[AUTO-PRINT] Feature disabled');
              }

              const expiresAt = Date.now() + 15_000;
              setNewOrderUntil((prev) => {
                const out = { ...(prev || {}) };
                for (const k of newKeys) {
                  if (!out[k]) out[k] = expiresAt;
                }
                return out;
              });

              window.setTimeout(() => {
                try {
                  setNewOrderUntil((prev) => {
                    const now = Date.now();
                    const out = {};
                    for (const [k, until] of Object.entries(prev || {})) {
                      if (Number(until) > now) out[k] = until;
                    }
                    return out;
                  });
                } catch {
                  // ignore
                }
              }, 15_100);
            }
          } else {
            hasLoadedQueueOnceRef.current = true;
          }

          prevQueueKeysRef.current = nextKeys;

          // Update the newest-timestamp baseline AFTER the new-arrival check above,
          // so the next fetch compares against what we knew before this one.
          try {
            let maxTs = Number(maxSeenOrderTsRef.current) || 0;
            for (const o of nextOrders) {
              const ts = Number(o?.order_timestamp) || 0;
              if (ts > maxTs) maxTs = ts;
            }
            maxSeenOrderTsRef.current = maxTs;
          } catch {
            // ignore
          }
        } else {
          prevQueueKeysRef.current = new Set(nextOrders.map(orderKey));
        }
      } catch {
        // ignore
      }

      setOrders(nextOrders);
      setCursor(String(nextCursor || ''));
      if (!background) {
        const tookMs = Date.now() - startedAt;
        const stoppedEarly = Boolean(nextCursor) && (nextOrders.length >= maxOrders || tookMs > maxMs);
        setStatusMessage(`Loaded ${nextOrders.length} order(s).${stoppedEarly ? ' (More available. Narrow filters or use Load more.)' : ''}`);
      }
    } catch (e) {
      if (!background) {
        setError(e?.message || 'Failed to load POS queue');
        if (reset) {
          setOrders([]);
          setCursor('');
          setNewOrderUntil({});
        }
      }
    } finally {
      if (background) {
        autoRefreshInFlightRef.current = false;
      } else {
        setBusy(false);
        setLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'POS Queue');
        if (!access.allowed) {
          navigate('/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');
        await Promise.all([loadPrintingSettings(), loadPrintBranding(), loadLocations()]);
        await fetchQueue({ reset: true });

        try {
          const qs = new URLSearchParams(window.location.search || '');
          const ok = String(qs.get('ok') || '').toLowerCase();
          const orderNumber = String(qs.get('order_number') || '').trim();
          const err = String(qs.get('error_code') || '').trim();
          if (ok === 'true' && orderNumber) {
            setStatusMessage(`Payment recorded for order ${orderNumber}.`);
          } else if (ok === 'false' && (orderNumber || err)) {
            setError(`Payment not completed${orderNumber ? ` for order ${orderNumber}` : ''}${err ? ` (${err})` : ''}.`);
          }
        } catch {
          // ignore
        }
      } catch {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (loading) return;
    fetchQueue({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookbackDays, paymentStatusFilter, orderStatusFilter, salesChannelFilter, locationFilter]);

  useEffect(() => {
    if (loading) return;
    if (dateMode !== 'preset') return;
    const next = presetKeyToDays(presetKey, customDays);
    if (next !== lookbackDays) setLookbackDays(next);
  }, [loading, dateMode, presetKey, customDays, lookbackDays]);

  useEffect(() => {
    if (loading) return;
    const intervalMs = 15_000;
    const id = window.setInterval(() => {
      try {
        if (detailsOrder) return;
        fetchQueue({ reset: true, background: true });
      } catch {
        // ignore
      }
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [loading, detailsOrder, lookbackDays, paymentStatusFilter, orderStatusFilter, salesChannelFilter, locationFilter]);

  useEffect(() => {
    if (dateMode === 'today') {
      setPresetKey('1d');
      setLookbackDays(1);
    }
  }, [dateMode]);

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-receipt"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>POS Queue</h1>
            <p>Paid online POS orders and readiness status</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username || 'Admin'}
          </span>
          <button onClick={() => Auth.signOut().then(() => navigate('/admin/login'))} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        {statusMessage ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage}
          </div>
        ) : null}

        <div className="products-list-container">
          <div className="pos-toolbar">
            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Channel</label>
              <select
                className="pos-input"
                value={salesChannelFilter}
                onChange={(e) => setSalesChannelFilter(String(e.target.value || 'all'))}
                disabled={busy || loadingMore}
                style={{ minWidth: 160 }}
              >
                <option value="all">ALL</option>
                <option value="pos">POS</option>
                <option value="marketplace">MARKETPLACE</option>
              </select>
            </div>

            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Payment</label>
              <select
                className="pos-input"
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(String(e.target.value || 'any'))}
                disabled={busy || loadingMore}
                style={{ minWidth: 160 }}
              >
                <option value="any">ALL</option>
                <option value="paid">PAID</option>
                <option value="pending_payment">PENDING</option>
              </select>
            </div>

            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Order</label>
              <select
                className="pos-input"
                value={orderStatusFilter}
                onChange={(e) => setOrderStatusFilter(String(e.target.value || 'active'))}
                disabled={busy || loadingMore}
                style={{ minWidth: 200 }}
              >
                <option value="active">ACTIVE</option>
                <option value="OPEN">OPEN</option>
                <option value="READY FOR PICKUP">READY FOR PICKUP</option>
                <option value="PICKUP COMPLETE">PICKUP COMPLETE</option>
                <option value="READY FOR DELIVERY">READY FOR DELIVERY</option>
                <option value="OUT FOR DELIVERY">OUT FOR DELIVERY</option>
                <option value="DELIVERED">DELIVERED</option>
                <option value="CANCELLED">CANCELLED</option>
                <option value="all">ALL</option>
              </select>
            </div>

            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Location</label>
              <select
                className="pos-input"
                value={locationFilter}
                onChange={(e) => setLocationFilter(String(e.target.value || 'all'))}
                disabled={busy || loadingMore}
                style={{ minWidth: 200 }}
              >
                <option value="all">ALL LOCATIONS</option>
                {locations.map((loc) => (
                  <option key={loc.location_id} value={loc.location_id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="pos-toolbar-group">
              <label className="pos-toolbar-label">Date</label>
              <div className="pos-chip-row">
                <button
                  type="button"
                  className={`pos-chip ${dateMode === 'today' ? 'active' : ''}`}
                  onClick={() => setDateMode('today')}
                  disabled={busy || loadingMore}
                >
                  TODAY
                </button>
                <button
                  type="button"
                  className={`pos-chip ${dateMode === 'preset' ? 'active' : ''}`}
                  onClick={() => setDateMode('preset')}
                  disabled={busy || loadingMore}
                >
                  PRESET
                </button>
              </div>
            </div>

            {dateMode === 'preset' ? (
              <div className="pos-toolbar-group">
                <label className="pos-toolbar-label">Range</label>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    className="pos-input"
                    value={presetKey}
                    onChange={(e) => setPresetKey(String(e.target.value || '7d'))}
                    disabled={busy || loadingMore}
                    style={{ minWidth: 220 }}
                  >
                    <option value="1d">{presetKeyToLabel('1d')}</option>
                    <option value="7d">{presetKeyToLabel('7d')}</option>
                    <option value="1m">{presetKeyToLabel('1m')}</option>
                    <option value="6m">{presetKeyToLabel('6m')}</option>
                    <option value="ytd">{presetKeyToLabel('ytd')}</option>
                    <option value="all">{presetKeyToLabel('all')}</option>
                    <option value="custom">{presetKeyToLabel('custom')}</option>
                  </select>

                  {String(presetKey || '').trim().toLowerCase() === 'custom' ? (
                    <input
                      className="pos-input"
                      type="number"
                      min={1}
                      max={365}
                      value={customDays}
                      onChange={(e) => setCustomDays(String(e.target.value || '7'))}
                      disabled={busy || loadingMore}
                      style={{ minWidth: 140 }}
                    />
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="pos-toolbar-group" style={{ minWidth: 240 }}>
              <label className="pos-toolbar-label">Search</label>
              <input
                className="pos-input"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Order #, name, phone, email"
                disabled={busy || loadingMore}
              />
            </div>

            <div className="pos-toolbar-actions">
              <button type="button" className="btn-primary" onClick={() => fetchQueue({ reset: true })} disabled={busy || loadingMore}>
                Refresh
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => fetchQueue({ reset: false })}
                disabled={busy || loadingMore || !hasMore}
              >
                Load more
              </button>
            </div>
          </div>

          {filteredOrders.length === 0 ? (
            <div className="pos-empty">
              <div style={{ fontWeight: 900, color: '#0f172a' }}>No orders</div>
              <div className="field-hint">Try changing the lookback window or payment filter.</div>
            </div>
          ) : (
            <div className="pos-order-list">
              {filteredOrders.map((o) => {
                const orderNumber = String(o?.order_number || '');
                const k = `${String(orderNumber || '').trim()}-${String(o?.order_timestamp || '')}`;
                const isNew = Date.now() < Number(newOrderUntil?.[k] || 0);
                const placedAt = formatDateTimeEastern(o?.order_timestamp);
                const ownTaxEnabled =
                  Boolean(o?.posTaxEnabled ?? posTaxEnabled) &&
                  String(o?.posTaxCollectionMode ?? posTaxCollectionMode ?? 'PROVIDER')
                    .trim()
                    .toUpperCase() === 'OWN';
                const ratePercent = Number(o?.posTaxRatePercent ?? posTaxRatePercent);

                const orderSubtotalCents = safeInt(o?.subtotal_cents, 0);
                const orderTaxCents = safeInt(o?.tax_cents, 0);
                const hasStoredBreakdown = Number.isFinite(Number(o?.subtotal_cents)) && Number.isFinite(Number(o?.tax_cents));

                const subtotalCents = hasStoredBreakdown ? orderSubtotalCents : safeInt(o?.amount_total_cents, 0);
                const rawTotalCents = safeInt(o?.amount_total_cents, 0);
                const storedDiscountCents = safeInt(o?.discount_cents ?? o?.discount_amount_cents ?? o?.customerSubmittedSnapshot?.discount_cents, 0);
                const taxFromStored = hasStoredBreakdown ? orderTaxCents : 0;
                const impliedDiscountCents = (hasStoredBreakdown && rawTotalCents > 0)
                  ? Math.max(0, Math.min(subtotalCents, subtotalCents + taxFromStored - rawTotalCents))
                  : 0;
                const discountCents = Math.max(0, Math.min(subtotalCents, Math.max(storedDiscountCents, impliedDiscountCents)));
                const taxableSubtotalCents = Math.max(0, subtotalCents - discountCents);
                const { taxCents: computedTaxCents, totalCents: computedTotalCents } = ownTaxEnabled
                  ? computeOwnTax(taxableSubtotalCents, ratePercent)
                  : { taxCents: 0, totalCents: taxableSubtotalCents };

                const taxCents = hasStoredBreakdown ? taxFromStored : computedTaxCents;
                const totalCents = hasStoredBreakdown ? (taxableSubtotalCents + taxCents) : computedTotalCents;

                const total = formatMoney(o?.posFinalTotalMoney, o?.amount_total_cents);
                const notes = String(o?.customerSubmittedSnapshot?.notes || '').trim();
                const customerName = String(o?.customer_name || '').trim();
                const customerEmail = String(o?.customer_email || '').trim();
                const customerPhone = String(o?.customer_phone || '').trim();
                const notifiedAt = o?.posPaidNotifiedAt ? formatDateTimeEastern(o.posPaidNotifiedAt) : '';
                const smsStatus = String(o?.posPaidNotifyResult?.sms || '');
                const emailStatus = String(o?.posPaidNotifyResult?.email || '');
                const paymentLinkUrl = String(o?.posPaymentLink?.href || o?.posPaymentLink?.long_url || o?.posPaymentLink?.url || '').trim();
                const paymentStatus = String(o?.payment_status || 'unknown');
                const posProvider = String(o?.posProvider || '').trim().toUpperCase();
                const providerLabel = posProvider === 'CLOVER' ? 'Clover' : posProvider === 'SQUARE' ? 'Square' : 'POS';
                const salesChannel = String(o?.salesChannel || '').trim().toUpperCase();
                const marketplaceProvider = String(o?.marketplaceProvider || '').trim().toUpperCase();
                const isUberEatsOrder =
                  String(o?.order_source || '').trim().toLowerCase() === 'ubereats' ||
                  marketplaceProvider === 'UBEREATS';
                const isDoorDashOrder =
                  String(o?.order_source || '').trim().toLowerCase() === 'doordash' ||
                  marketplaceProvider === 'DOORDASH';
                const posOrderState = String(o?.posOrderState || o?.squareOrderState || '').trim();
                const rawFulfillment = String(o?.adminFulfillmentStatus || o?.posFulfillmentState || o?.squareFulfillmentState || '').trim();
                const oIsDelivery = String(o?.fulfillment_type || '').trim().toLowerCase() === 'delivery';
                const fulfillmentLabel = formatFulfillmentStatus(rawFulfillment, oIsDelivery);
                const paymentMode = String(o?.posPaymentMode || '').trim().toUpperCase();
                const canTakePayment =
                  posProvider === 'SQUARE' && paymentMode === 'PAY_AT_POS' && paymentStatus.toLowerCase() === 'pending_payment';
                const canMarkAsPaid = paymentStatus.toLowerCase() !== 'paid' && paymentStatus.toLowerCase() !== 'refunded';
                const summaryPhone = customerPhone ? maskPhone(customerPhone) : '';

                const orderStatus = String(o?.status || o?.order_status || '').trim().toUpperCase();
                const isCancelled = orderStatus === 'CANCELLED' || orderStatus === 'CANCELED';
                const isPickupComplete = String(fulfillmentLabel || '').trim().toUpperCase() === 'PICKUP COMPLETE';
                const canCancel = !isCancelled && !isPickupComplete;

                // Uber Eats orders are pending/unaccepted until staff accept them.
                // Show accept/reject only while pending and not cancelled (Req 12.5).
                const canAcceptUberEats =
                  isUberEatsOrder && !isCancelled && (orderStatus === 'PENDING' || orderStatus === '');

                // DoorDash orders are pending/unaccepted until staff accept them.
                // Show accept/reject only while pending and not cancelled (Req 15.6).
                const canAcceptDoorDash =
                  isDoorDashOrder && !isCancelled && (orderStatus === 'PENDING' || orderStatus === '');
                const experienceFlag = String(o?.experience_flag || '').trim();

                // Single channel descriptor (lives in the subtitle, not as a loud badge).
                // Avoids the duplicate "UBEREATS / 🛵 UBER EATS" pair that cluttered the row.
                const channelLabel = isUberEatsOrder
                  ? 'Uber Eats'
                  : isDoorDashOrder
                    ? 'DoorDash'
                    : salesChannel === 'MARKETPLACE'
                      ? (marketplaceProvider || 'Marketplace')
                      : '';
                const channelColor = isUberEatsOrder ? '#06C167' : isDoorDashOrder ? '#FF3008' : '#6b7280';

                // SLA blinker band for OPEN/ACTIVE orders (null when feature off / not OPEN).
                // Mirror the queue's notion of "open": explicit OPEN fulfillment, or a
                // pending-payment order that hasn't progressed to a later fulfillment state.
                // Treat any order without a later fulfillment status as OPEN for
                // SLA tracking, regardless of payment method (online-paid,
                // pay-at-store) or fulfillment type (pickup, delivery).
                // Completed/cancelled orders always carry a non-empty fulfillment
                // label (e.g. PICKUP COMPLETE, DELIVERED, CANCELLED), so they are
                // naturally excluded.
                const effectiveFulfillmentForSla = fulfillmentLabel || 'OPEN';
                const slaBand = computeSlaBand(slaConfig, effectiveFulfillmentForSla, o?.order_timestamp, slaNowMs);
                const slaColor = slaBand === 'green' ? '#16a34a' : slaBand === 'orange' ? '#ea580c' : slaBand === 'red' ? '#dc2626' : '#6b7280';
                const slaOpenMinutes = slaBand
                  ? Math.max(0, Math.floor((slaNowMs / 1000 - Number(o?.order_timestamp)) / 60))
                  : null;

                return (
                  <details
                    key={`${orderNumber}-${String(o?.order_timestamp || '')}`}
                    className={`pos-order-disclosure${isNew ? ' pos-order-disclosure-new' : ''}`}
                  >
                    <summary className="pos-order-summary">
                      {slaBand ? (
                        <span
                          className={`pos-sla-light pos-sla-light-${slaBand}`}
                          title={`SLA: ${slaBand.toUpperCase()} — open ${slaConfig.greenMinutes}/${slaConfig.orangeMinutes}/${slaConfig.redMinutes} min thresholds`}
                          aria-label={`SLA ${slaBand}`}
                        />
                      ) : null}
                      <div className="pos-order-summary-main">
                        <button
                          type="button"
                          className="pos-order-summary-title-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDetailsOrder(o);
                          }}
                          disabled={!orderNumber}
                        >
                          Order {orderNumber}
                        </button>
                        <div className="pos-order-summary-sub">
                          {placedAt}
                          {channelLabel ? (
                            <span style={{ marginLeft: 8, fontWeight: 600, color: channelColor }}>· {channelLabel}</span>
                          ) : null}
                          <span style={{ marginLeft: 8, color: '#6b7280' }}>
                            · {oIsDelivery ? 'Delivery' : 'Pickup'}
                          </span>
                        </div>
                        {slaBand ? (
                          <div className="pos-sla-msg" style={{ color: slaColor, fontWeight: 600, fontSize: '0.8125rem', marginTop: 2 }}>
                            Order has been in OPEN status for {slaOpenMinutes} {slaOpenMinutes === 1 ? 'minute' : 'minutes'}
                          </div>
                        ) : null}
                      </div>

                      <div className="pos-order-summary-customer">
                        <div className="pos-order-summary-customer-name">{customerName || '—'}</div>
                        {summaryPhone ? <div className="pos-order-summary-customer-phone">{summaryPhone}</div> : null}
                        {o?.fulfillment_type === 'delivery' && o?.delivery_address ? (
                          <div className="pos-order-summary-customer-phone" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                            <i className="fas fa-map-marker-alt" style={{ marginRight: '4px' }}></i>
                            {o.delivery_address.city}, {o.delivery_address.state}
                          </div>
                        ) : null}
                      </div>

                      <div className="pos-order-summary-badges">
                        <span className={`pos-badge ${paymentStatus.toLowerCase() === 'paid' ? 'pos-badge-paid' : 'pos-badge-neutral'}`}>
                          {paymentStatus}
                        </span>
                        {isDoorDashOrder && experienceFlag ? (
                          <span className="pos-badge pos-badge-neutral" title="DoorDash experience">
                            {experienceFlag}
                          </span>
                        ) : null}
                        {fulfillmentLabel ? <span className="pos-badge pos-badge-neutral">{fulfillmentLabel}</span> : null}
                        {o?.location_name ? (
                          <span className="pos-badge pos-badge-neutral" title={`Location: ${o.location_name}`}>
                            <i className="fas fa-map-marker-alt" style={{ marginRight: '4px' }}></i>
                            {o.location_name}
                          </span>
                        ) : null}
                        {isNew ? <span className="pos-badge pos-badge-new">NEW</span> : null}
                        <button
                          type="button"
                          className="pos-chip"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onPrintOrder(o, { silent: false });
                          }}
                          disabled={busy || loadingMore}
                        >
                          PRINT
                        </button>
                    </div>

                      <div className="pos-order-summary-chevron" aria-hidden="true" />
                    </summary>

                    <div className="pos-order-details">
                      <div className="pos-order-card">
                        <div className="pos-order-meta">
                          <div>
                            <div className="pos-order-meta-label">{ownTaxEnabled ? 'Estimated total' : 'Total'}</div>
                            <div className={`pos-order-meta-value${ownTaxEnabled ? ' pos-order-total-highlight' : ''}`}>{total}</div>
                            {(() => {
                              const tipCents = safeInt(o?.tip_cents, 0);
                              const showBreakdown = ownTaxEnabled || discountCents || tipCents > 0;
                              if (!showBreakdown) return null;
                              return (
                                <div className="pos-order-total-breakdown">
                                  <div className="pos-order-total-row">
                                    <span>Subtotal</span>
                                    <span>{formatMoney(null, subtotalCents)}</span>
                                  </div>
                                  {discountCents ? (
                                    <div className="pos-order-total-row">
                                      <span>Discount</span>
                                      <span>-{formatMoney(null, discountCents)}</span>
                                    </div>
                                  ) : null}
                                  {ownTaxEnabled ? (
                                    <div className="pos-order-total-row">
                                      <span>Estimated tax ({Number(ratePercent) || 0}%)</span>
                                      <span>{formatMoney(null, taxCents)}</span>
                                    </div>
                                  ) : null}
                                  {tipCents > 0 ? (
                                    <div className="pos-order-total-row">
                                      <span>Tip</span>
                                      <span>{formatMoney(null, tipCents)}</span>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })()}
                          </div>
                          <div>
                            <div className="pos-order-meta-label">Customer</div>
                            <div className="pos-order-meta-value">{customerName || '—'}</div>
                            {customerPhone ? <div className="field-hint">Phone: {maskPhone(customerPhone)}</div> : null}
                            {customerEmail ? <div className="field-hint">Email: {customerEmail}</div> : null}
                          </div>
                          {!isUberEatsOrder && !isDoorDashOrder && o?.fulfillment_type === 'delivery' && o?.delivery_address ? (
                            <div>
                              <div className="pos-order-meta-label">
                                <i className="fas fa-truck" style={{ marginRight: '6px', color: '#3b82f6' }}></i>
                                Delivery Address
                              </div>
                              <div className="pos-order-meta-value">
                                {o.delivery_address.street}
                                {o.delivery_address.apt_unit ? `, ${o.delivery_address.apt_unit}` : ''}
                              </div>
                              <div className="field-hint">
                                {o.delivery_address.city}, {o.delivery_address.state} {o.delivery_address.zip}
                              </div>
                              {o?.delivery_instructions ? (
                                <div className="field-hint" style={{ marginTop: '4px' }}>
                                  <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                                  <strong>Instructions:</strong> {o.delivery_instructions}
                                </div>
                              ) : null}
                              {o?.delivery_fee_cents > 0 ? (
                                <div className="field-hint" style={{ marginTop: '4px', color: '#059669' }}>
                                  <i className="fas fa-dollar-sign" style={{ marginRight: '4px' }}></i>
                                  <strong>Delivery Fee:</strong> {formatMoney(null, o.delivery_fee_cents)}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          {isUberEatsOrder ? (
                            <div style={{ gridColumn: '1 / -1', marginTop: 4, padding: 12, backgroundColor: '#E9FBF1', borderRadius: 8, border: '1px solid #06C167' }}>
                              <div style={{ fontWeight: 700, color: '#067D43', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>🛵</span>
                                <span>Uber Eats Delivery Order</span>
                              </div>
                              {o?.delivery_address ? (
                                <div>
                                  <div className="pos-order-meta-value">
                                    {o.delivery_address.street}
                                    {o.delivery_address.apt_unit ? `, ${o.delivery_address.apt_unit}` : ''}
                                  </div>
                                  <div className="field-hint">
                                    {o.delivery_address.city}, {o.delivery_address.state} {o.delivery_address.zip}
                                  </div>
                                </div>
                              ) : null}
                              {o?.delivery_instructions ? (
                                <div className="field-hint" style={{ marginTop: 6 }}>
                                  <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                                  <strong>Instructions:</strong> {o.delivery_instructions}
                                </div>
                              ) : null}
                              {o?.requested_delivery_time ? (
                                <div className="field-hint" style={{ marginTop: 6 }}>
                                  <strong>Requested:</strong> {formatDateTimeEastern(o.requested_delivery_time)}
                                </div>
                              ) : null}
                              {o?.ubereats_order_id ? (
                                <div className="field-hint" style={{ marginTop: 6, color: '#64748B' }}>
                                  Uber Eats Order ID: {o.ubereats_order_id}
                                </div>
                              ) : null}
                              {canAcceptUberEats ? (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                                  <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => acceptUberEatsOrder(o)}
                                    disabled={busy || loadingMore}
                                    style={{ backgroundColor: '#06C167', borderColor: '#06C167' }}
                                  >
                                    Accept
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => rejectUberEatsOrder(o)}
                                    disabled={busy || loadingMore}
                                    style={{ borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                                  >
                                    Reject
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          {isDoorDashOrder ? (
                            <div style={{ gridColumn: '1 / -1', marginTop: 4, padding: 12, backgroundColor: '#FFF0ED', borderRadius: 8, border: '1px solid #FF3008' }}>
                              <div style={{ fontWeight: 700, color: '#B3210A', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>🚗</span>
                                <span>DoorDash Delivery Order</span>
                                {experienceFlag ? (
                                  <span className="pos-badge pos-badge-neutral" style={{ marginLeft: 4 }}>{experienceFlag}</span>
                                ) : null}
                              </div>
                              {o?.delivery_address ? (
                                <div>
                                  <div className="pos-order-meta-value">
                                    {o.delivery_address.street}
                                    {o.delivery_address.apt_unit ? `, ${o.delivery_address.apt_unit}` : ''}
                                  </div>
                                  <div className="field-hint">
                                    {o.delivery_address.city}, {o.delivery_address.state} {o.delivery_address.zip}
                                  </div>
                                </div>
                              ) : null}
                              {o?.special_instructions ? (
                                <div className="field-hint" style={{ marginTop: 6 }}>
                                  <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                                  <strong>Special Instructions:</strong> {o.special_instructions}
                                </div>
                              ) : null}
                              {o?.delivery_instructions ? (
                                <div className="field-hint" style={{ marginTop: 6 }}>
                                  <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                                  <strong>Instructions:</strong> {o.delivery_instructions}
                                </div>
                              ) : null}
                              {o?.requested_delivery_time ? (
                                <div className="field-hint" style={{ marginTop: 6 }}>
                                  <strong>Requested:</strong> {formatDateTimeEastern(o.requested_delivery_time)}
                                </div>
                              ) : null}
                              {o?.merchant_tip > 0 ? (
                                <div className="field-hint" style={{ marginTop: 6, color: '#059669' }}>
                                  <i className="fas fa-dollar-sign" style={{ marginRight: '4px' }}></i>
                                  <strong>Merchant Tip:</strong> {formatMoney(null, o.merchant_tip)}
                                </div>
                              ) : null}
                              {o?.doordash_order_id ? (
                                <div className="field-hint" style={{ marginTop: 6, color: '#64748B' }}>
                                  DoorDash Order ID: {o.doordash_order_id}
                                </div>
                              ) : null}
                              {canAcceptDoorDash ? (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                                  <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={() => acceptDoorDashOrder(o)}
                                    disabled={busy || loadingMore}
                                    style={{ backgroundColor: '#FF3008', borderColor: '#FF3008' }}
                                  >
                                    Accept
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => rejectDoorDashOrder(o)}
                                    disabled={busy || loadingMore}
                                    style={{ borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                                  >
                                    Reject
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                          <div>
                            <div className="pos-order-meta-label">{providerLabel}</div>
                            <div className="pos-order-meta-value">{o?.posOrderId ? String(o.posOrderId) : '—'}</div>
                            {posOrderState ? <div className="field-hint">State: {posOrderState}</div> : null}
                          </div>

                          <div>
                            <div className="pos-order-meta-label">Set fulfillment</div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'OPEN')} disabled={busy || loadingMore || isCancelled}>
                                OPEN
                              </button>
                              {oIsDelivery ? (
                                <>
                                  <button
                                    type="button"
                                    className="pos-chip"
                                    onClick={() => updateFulfillment(o, 'READY_FOR_DELIVERY')}
                                    disabled={busy || loadingMore || isCancelled}
                                  >
                                    READY
                                  </button>
                                  <button
                                    type="button"
                                    className="pos-chip"
                                    onClick={() => updateFulfillment(o, 'OUT_FOR_DELIVERY')}
                                    disabled={busy || loadingMore || isCancelled}
                                  >
                                    OUT
                                  </button>
                                  <button
                                    type="button"
                                    className="pos-chip"
                                    onClick={() => updateFulfillment(o, 'DELIVERED')}
                                    disabled={busy || loadingMore || isCancelled}
                                  >
                                    DELIVERED
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="pos-chip"
                                    onClick={() => updateFulfillment(o, 'READY_FOR_PICKUP')}
                                    disabled={busy || loadingMore || isCancelled}
                                  >
                                    READY
                                  </button>
                                  <button
                                    type="button"
                                    className="pos-chip"
                                    onClick={() => updateFulfillment(o, 'PICKUP_COMPLETE')}
                                    disabled={busy || loadingMore || isCancelled}
                                  >
                                    PICKED UP
                                  </button>
                                </>
                              )}
                              <button
                                type="button"
                                className="pos-chip"
                                onClick={() => updateFulfillment(o, 'CANCELLED')}
                                disabled={busy || loadingMore || isCancelled}
                                style={{ borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                              >
                                CANCEL
                              </button>
                            </div>
                            {o?.squareFulfillmentSyncAttempted === true && o?.squareFulfillmentSyncOk === false && String(o?.squareFulfillmentSyncError || '').trim() ? (
                              <div className="field-hint" style={{ marginTop: 6 }}>
                                Square sync failed: {String(o.squareFulfillmentSyncError)}
                              </div>
                            ) : null}
                          </div>
                          <div>
                            <div className="pos-order-meta-label">Notify</div>
                            <div className="pos-order-meta-value">email {emailStatus || 'n/a'}, sms {smsStatus || 'n/a'}</div>
                            {notifiedAt ? <div className="field-hint">Notified: {notifiedAt}</div> : null}
                          </div>
                        </div>

                        {paymentLinkUrl ? (
                          <div className="pos-order-links">
                            <a className="pos-link" href={paymentLinkUrl} target="_blank" rel="noreferrer">
                              Open payment link
                            </a>
                          </div>
                        ) : null}

                        <div className="pos-order-links">
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => onPrintOrder(o, { silent: false })}
                            disabled={busy || loadingMore}
                            style={{ width: '100%', marginTop: 10 }}
                          >
                            Print receipt
                          </button>
                        </div>

                        {canTakePayment ? (
                          <div className="pos-order-links">
                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => takePayment(o)}
                              disabled={busy || loadingMore}
                              style={{ width: '100%', marginTop: 10 }}
                            >
                              Take payment (Square POS)
                            </button>
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              Opens Square POS on this device for card-present or cash.
                            </div>
                          </div>
                        ) : null}

                        {canMarkAsPaid ? (
                          <div className="pos-order-links">
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => markAsPaid(o)}
                              disabled={busy || loadingMore}
                              style={{ width: '100%', marginTop: 10 }}
                            >
                              Mark as Paid
                            </button>
                          </div>
                        ) : null}

                        {canCancel ? (
                          <div className="pos-order-links">
                            <div className="field-hint" style={{ marginTop: 10 }}>
                              {posProvider === 'SQUARE'
                                ? 'Cancels the Square fulfillment (queue). Refunds are automatic only for paid-online orders.'
                                : `Cancels the order in EzSell. Refunds/cancellations in ${providerLabel} must be handled manually in ${providerLabel}.`}
                            </div>
                            <button
                              type="button"
                              className="btn-secondary"
                              onClick={() => cancelOrder(o)}
                              disabled={busy || loadingMore}
                              style={{ width: '100%', marginTop: 10, borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                            >
                              Cancel order
                            </button>
                          </div>
                        ) : (
                          <div className="pos-order-links">
                            <div className="field-hint" style={{ marginTop: 10 }}>
                              {isCancelled ? 'Order is cancelled.' : isPickupComplete ? 'Pickup complete — cancellation disabled.' : ''}
                            </div>
                          </div>
                        )}

                        {notes ? (
                          <div className="pos-order-notes">
                            <strong>Order Notes:</strong> {notes}
                          </div>
                        ) : null}

                        {Array.isArray(o?.customerSubmittedSnapshot?.items) && o.customerSubmittedSnapshot.items.length > 0 ? (
                          <div className="pos-order-items">
                            <div style={{ fontWeight: 900, color: '#0f172a' }}>Items</div>
                            <ul>
                              {o.customerSubmittedSnapshot.items.slice(0, 20).map((it, idx) => (
                                <li key={`${orderNumber}-item-${idx}`}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    {String(it?.ImageURL || it?.image_url || it?.imageUrl || '').trim() ? (
                                      <img
                                        src={String(it?.ImageURL || it?.image_url || it?.imageUrl)}
                                        alt=""
                                        style={{
                                          width: 34,
                                          height: 34,
                                          borderRadius: 10,
                                          objectFit: 'cover',
                                          border: '1px solid rgba(15, 23, 42, 0.10)',
                                        }}
                                        loading="lazy"
                                        referrerPolicy="no-referrer"
                                        onError={(e) => {
                                          try {
                                            e.currentTarget.style.display = 'none';
                                          } catch {
                                            // ignore
                                          }
                                        }}
                                      />
                                    ) : null}
                                    <span style={{ minWidth: 0 }}>
                                      <span>
                                        {String(it?.name || it?.ProductName || 'Item')} x{String(it?.Quantity || it?.quantity || 1)}
                                      </span>
                                      {String(it?.item_notes || it?.itemNotes || '').trim() ? (
                                        <div className="pos-item-notes-highlight">
                                          <strong>Item Notes:</strong> {String(it?.item_notes || it?.itemNotes || '').trim()}
                                        </div>
                                      ) : null}
                                    </span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </details>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {detailsOrder ? (
        <div
          className="fulfillment-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setDetailsOrder(null);
          }}
        >
          <div className="fulfillment-modal" style={{ width: 'min(860px, 100%)' }}>
            <div className="fulfillment-modal-header">
              <h3>Order {String(detailsOrder?.order_number || '—')}</h3>
              <button type="button" className="admin-mini-btn" onClick={() => onPrintOrder(detailsOrder, { silent: false })} disabled={busy}>
                Print
              </button>
              <button type="button" className="admin-mini-btn" onClick={() => setDetailsOrder(null)} disabled={busy}>
                Close
              </button>
            </div>
            <div className="fulfillment-modal-body">
              {(() => {
                const o = detailsOrder;
                const ownTaxEnabled =
                  Boolean(o?.posTaxEnabled ?? posTaxEnabled) &&
                  String(o?.posTaxCollectionMode ?? posTaxCollectionMode ?? 'PROVIDER')
                    .trim()
                    .toUpperCase() === 'OWN';
                const ratePercent = Number(o?.posTaxRatePercent ?? posTaxRatePercent);

                const orderSubtotalCents = safeInt(o?.subtotal_cents, 0);
                const orderTaxCents = safeInt(o?.tax_cents, 0);
                const hasStoredBreakdown = Number.isFinite(Number(o?.subtotal_cents)) && Number.isFinite(Number(o?.tax_cents));

                const subtotalCents = hasStoredBreakdown ? orderSubtotalCents : safeInt(o?.amount_total_cents, 0);
                const rawTotalCents = safeInt(o?.amount_total_cents, 0);
                const storedDiscountCents = safeInt(o?.discount_cents ?? o?.discount_amount_cents ?? o?.customerSubmittedSnapshot?.discount_cents, 0);
                const taxFromStored = hasStoredBreakdown ? orderTaxCents : 0;
                const impliedDiscountCents = (hasStoredBreakdown && rawTotalCents > 0)
                  ? Math.max(0, Math.min(subtotalCents, subtotalCents + taxFromStored - rawTotalCents))
                  : 0;
                const discountCents = Math.max(0, Math.min(subtotalCents, Math.max(storedDiscountCents, impliedDiscountCents)));
                const taxableSubtotalCents = Math.max(0, subtotalCents - discountCents);
                const { taxCents: computedTaxCents, totalCents: computedTotalCents } = ownTaxEnabled
                  ? computeOwnTax(taxableSubtotalCents, ratePercent)
                  : { taxCents: 0, totalCents: taxableSubtotalCents };

                const taxCents = hasStoredBreakdown ? taxFromStored : computedTaxCents;
                const totalCents = hasStoredBreakdown ? (taxableSubtotalCents + taxCents) : computedTotalCents;

                const total = formatMoney(o?.posFinalTotalMoney, o?.amount_total_cents);
                const placedAt = formatDateTimeEastern(o?.order_timestamp);
                const customerName = String(o?.customer_name || '').trim();
                const customerEmail = String(o?.customer_email || '').trim();
                const customerPhone = String(o?.customer_phone || '').trim();
                const notes = String(o?.customerSubmittedSnapshot?.notes || '').trim();
                const paymentStatus = String(o?.payment_status || 'unknown').trim();
                const paymentMode = String(o?.posPaymentMode || '').trim();
                const posProvider = String(o?.posProvider || '').trim().toUpperCase();
                const providerLabel = posProvider === 'CLOVER' ? 'Clover' : posProvider === 'SQUARE' ? 'Square' : 'POS';
                const posOrderState = String(o?.posOrderState || o?.squareOrderState || '').trim();
                const rawFulfillment = String(o?.adminFulfillmentStatus || o?.posFulfillmentState || o?.squareFulfillmentState || '').trim();
                const oIsDelivery = String(o?.fulfillment_type || '').trim().toLowerCase() === 'delivery';
                const fulfillmentLabel = formatFulfillmentStatus(rawFulfillment, oIsDelivery);
                const paymentLinkUrl = String(o?.posPaymentLink?.href || o?.posPaymentLink?.long_url || o?.posPaymentLink?.url || '').trim();
                const notifyEmailStatus = String(o?.posPaidNotifyResult?.email || '').trim();
                const notifySmsStatus = String(o?.posPaidNotifyResult?.sms || '').trim();
                const notifiedAt = o?.posPaidNotifiedAt ? formatDateTimeEastern(o.posPaidNotifiedAt) : '';
                const items = Array.isArray(o?.customerSubmittedSnapshot?.items) ? o.customerSubmittedSnapshot.items : [];

                const orderStatus = String(o?.status || o?.order_status || '').trim().toUpperCase();
                const isCancelled = orderStatus === 'CANCELLED' || orderStatus === 'CANCELED';
                const isPickupComplete = String(fulfillmentLabel || '').trim().toUpperCase() === 'PICKUP COMPLETE';
                const canCancel = !isCancelled && !isPickupComplete;

                const isUberEatsOrder =
                  String(o?.order_source || '').trim().toLowerCase() === 'ubereats' ||
                  String(o?.marketplaceProvider || '').trim().toUpperCase() === 'UBEREATS';
                const canAcceptUberEats =
                  isUberEatsOrder && !isCancelled && (orderStatus === 'PENDING' || orderStatus === '');

                const isDoorDashOrder =
                  String(o?.order_source || '').trim().toLowerCase() === 'doordash' ||
                  String(o?.marketplaceProvider || '').trim().toUpperCase() === 'DOORDASH';
                const canAcceptDoorDash =
                  isDoorDashOrder && !isCancelled && (orderStatus === 'PENDING' || orderStatus === '');
                const experienceFlag = String(o?.experience_flag || '').trim();

                const paymentModeUpper = String(o?.posPaymentMode || '').trim().toUpperCase();
                const canTakePayment =
                  posProvider === 'SQUARE' && paymentModeUpper === 'PAY_AT_POS' && paymentStatus.toLowerCase() === 'pending_payment';
                const shouldAutoRefund = posProvider === 'SQUARE' && (paymentModeUpper === 'PAY_NOW' || !!paymentLinkUrl);
                const canMarkPaid =
                  !isCancelled && paymentStatus.toLowerCase() === 'pending_payment';

                return (
                  <>
                    <div className="pos-order-meta" style={{ marginTop: 0 }}>
                      <div>
                        <div className="pos-order-meta-label">Placed</div>
                        <div className="pos-order-meta-value">{placedAt}</div>
                      </div>
                      {isUberEatsOrder ? (
                        <div style={{ gridColumn: '1 / -1', padding: 12, backgroundColor: '#E9FBF1', borderRadius: 8, border: '1px solid #06C167' }}>
                          <div style={{ fontWeight: 700, color: '#067D43', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🛵</span>
                            <span>Uber Eats Delivery Order</span>
                          </div>
                          {o?.delivery_address ? (
                            <div>
                              <div className="pos-order-meta-value">
                                {o.delivery_address.street}
                                {o.delivery_address.apt_unit ? `, ${o.delivery_address.apt_unit}` : ''}
                              </div>
                              <div className="field-hint">
                                {o.delivery_address.city}, {o.delivery_address.state} {o.delivery_address.zip}
                              </div>
                            </div>
                          ) : null}
                          {o?.delivery_instructions ? (
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                              <strong>Instructions:</strong> {o.delivery_instructions}
                            </div>
                          ) : null}
                          {o?.requested_delivery_time ? (
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              <strong>Requested:</strong> {formatDateTimeEastern(o.requested_delivery_time)}
                            </div>
                          ) : null}
                          {o?.ubereats_order_id ? (
                            <div className="field-hint" style={{ marginTop: 6, color: '#64748B' }}>
                              Uber Eats Order ID: {o.ubereats_order_id}
                            </div>
                          ) : null}
                          {canAcceptUberEats ? (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                              <button
                                type="button"
                                className="btn-primary"
                                onClick={() => acceptUberEatsOrder(o)}
                                disabled={busy || loadingMore}
                                style={{ backgroundColor: '#06C167', borderColor: '#06C167' }}
                              >
                                Accept
                              </button>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => rejectUberEatsOrder(o)}
                                disabled={busy || loadingMore}
                                style={{ borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                              >
                                Reject
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      {isDoorDashOrder ? (
                        <div style={{ gridColumn: '1 / -1', padding: 12, backgroundColor: '#FFF0ED', borderRadius: 8, border: '1px solid #FF3008' }}>
                          <div style={{ fontWeight: 700, color: '#B3210A', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span>🚗</span>
                            <span>DoorDash Delivery Order</span>
                            {experienceFlag ? (
                              <span className="pos-badge pos-badge-neutral" style={{ marginLeft: 4 }}>{experienceFlag}</span>
                            ) : null}
                          </div>
                          {o?.delivery_address ? (
                            <div>
                              <div className="pos-order-meta-value">
                                {o.delivery_address.street}
                                {o.delivery_address.apt_unit ? `, ${o.delivery_address.apt_unit}` : ''}
                              </div>
                              <div className="field-hint">
                                {o.delivery_address.city}, {o.delivery_address.state} {o.delivery_address.zip}
                              </div>
                            </div>
                          ) : null}
                          {o?.special_instructions ? (
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                              <strong>Special Instructions:</strong> {o.special_instructions}
                            </div>
                          ) : null}
                          {o?.delivery_instructions ? (
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                              <strong>Instructions:</strong> {o.delivery_instructions}
                            </div>
                          ) : null}
                          {o?.requested_delivery_time ? (
                            <div className="field-hint" style={{ marginTop: 6 }}>
                              <strong>Requested:</strong> {formatDateTimeEastern(o.requested_delivery_time)}
                            </div>
                          ) : null}
                          {o?.merchant_tip > 0 ? (
                            <div className="field-hint" style={{ marginTop: 6, color: '#059669' }}>
                              <i className="fas fa-dollar-sign" style={{ marginRight: '4px' }}></i>
                              <strong>Merchant Tip:</strong> {formatMoney(null, o.merchant_tip)}
                            </div>
                          ) : null}
                          {o?.doordash_order_id ? (
                            <div className="field-hint" style={{ marginTop: 6, color: '#64748B' }}>
                              DoorDash Order ID: {o.doordash_order_id}
                            </div>
                          ) : null}
                          {canAcceptDoorDash ? (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                              <button
                                type="button"
                                className="btn-primary"
                                onClick={() => acceptDoorDashOrder(o)}
                                disabled={busy || loadingMore}
                                style={{ backgroundColor: '#FF3008', borderColor: '#FF3008' }}
                              >
                                Accept
                              </button>
                              <button
                                type="button"
                                className="btn-secondary"
                                onClick={() => rejectDoorDashOrder(o)}
                                disabled={busy || loadingMore}
                                style={{ borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                              >
                                Reject
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      <div>
                        <div className="pos-order-meta-label">Payment</div>
                        <div className="pos-order-meta-value">{paymentStatus || 'unknown'}</div>
                        {paymentMode ? <div className="field-hint">Mode: {paymentMode}</div> : null}
                      </div>
                      <div>
                        <div className="pos-order-meta-label">Fulfillment</div>
                        <div className="pos-order-meta-value">{fulfillmentLabel || '—'}</div>
                        {o?.squareFulfillmentSyncAttempted === true && o?.squareFulfillmentSyncOk === false && String(o?.squareFulfillmentSyncError || '').trim() ? (
                          <div className="field-hint">Square sync failed: {String(o.squareFulfillmentSyncError)}</div>
                        ) : null}
                      </div>
                      <div>
                        <div className="pos-order-meta-label">Total</div>
                        <div className={`pos-order-meta-value${ownTaxEnabled ? ' pos-order-total-highlight' : ''}`}>{total}</div>
                        {ownTaxEnabled || discountCents ? (
                          <div className="pos-order-total-breakdown">
                            <div className="pos-order-total-row">
                              <span>Subtotal</span>
                              <span>{formatMoney(null, subtotalCents)}</span>
                            </div>
                            {discountCents ? (
                              <div className="pos-order-total-row">
                                <span>Discount</span>
                                <span>-{formatMoney(null, discountCents)}</span>
                              </div>
                            ) : null}
                            {ownTaxEnabled ? (
                              <div className="pos-order-total-row">
                                <span>Estimated tax ({Number(ratePercent) || 0}%)</span>
                                <span>{formatMoney(null, taxCents)}</span>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <div className="pos-order-meta-label">Customer</div>
                        <div className="pos-order-meta-value">{customerName || '—'}</div>
                        {customerPhone ? <div className="field-hint">Phone: {maskPhone(customerPhone)}</div> : null}
                        {customerEmail ? <div className="field-hint">Email: {customerEmail}</div> : null}
                      </div>
                      <div>
                        <div className="pos-order-meta-label">{providerLabel}</div>
                        <div className="pos-order-meta-value">{o?.posOrderId ? String(o.posOrderId) : '—'}</div>
                        {posOrderState ? <div className="field-hint">State: {posOrderState}</div> : null}
                      </div>

                      <div>
                        <div className="pos-order-meta-label">Set fulfillment</div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                          <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'OPEN')} disabled={busy || loadingMore || isCancelled}>
                            OPEN
                          </button>
                          {oIsDelivery ? (
                            <>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'READY_FOR_DELIVERY')} disabled={busy || loadingMore || isCancelled}>
                                READY
                              </button>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'OUT_FOR_DELIVERY')} disabled={busy || loadingMore || isCancelled}>
                                OUT
                              </button>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'DELIVERED')} disabled={busy || loadingMore || isCancelled}>
                                DELIVERED
                              </button>
                            </>
                          ) : (
                            <>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'READY_FOR_PICKUP')} disabled={busy || loadingMore || isCancelled}>
                                READY
                              </button>
                              <button type="button" className="pos-chip" onClick={() => updateFulfillment(o, 'PICKUP_COMPLETE')} disabled={busy || loadingMore || isCancelled}>
                                PICKED UP
                              </button>
                            </>
                          )}
                          <button
                            type="button"
                            className="pos-chip"
                            onClick={() => updateFulfillment(o, 'CANCELLED')}
                            disabled={busy || loadingMore || isCancelled}
                            style={{ borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                          >
                            CANCEL
                          </button>
                        </div>
                      </div>
                      <div>
                        <div className="pos-order-meta-label">Notify</div>
                        <div className="pos-order-meta-value">email {notifyEmailStatus || 'n/a'}, sms {notifySmsStatus || 'n/a'}</div>
                        {notifiedAt ? <div className="field-hint">Notified: {notifiedAt}</div> : null}
                      </div>
                    </div>

                    {paymentLinkUrl ? (
                      <div className="pos-order-links">
                        <a className="pos-link" href={paymentLinkUrl} target="_blank" rel="noreferrer">
                          Open payment link
                        </a>
                      </div>
                    ) : null}

                    {notes ? (
                      <div className="pos-order-notes">
                        <strong>Order Notes:</strong> {notes}
                      </div>
                    ) : null}

                    {items.length > 0 ? (
                      <div className="pos-order-items">
                        <div style={{ fontWeight: 900, color: '#0f172a' }}>Items</div>
                        <ul>
                          {items.slice(0, 20).map((it, idx) => {
                            const imageUrl = String(it?.ImageURL || it?.image_url || it?.imageUrl || '').trim();
                            const name = String(it?.name || it?.ProductName || 'Item');
                            const qty = String(it?.Quantity || it?.quantity || 1);
                            return (
                              <li key={`${String(detailsOrder?.order_number || '')}-item-${idx}`}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  {imageUrl ? (
                                    <img
                                      src={imageUrl}
                                      alt=""
                                      style={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: 10,
                                        objectFit: 'cover',
                                        border: '1px solid rgba(15, 23, 42, 0.10)',
                                      }}
                                      loading="lazy"
                                      referrerPolicy="no-referrer"
                                      onError={(e) => {
                                        try {
                                          e.currentTarget.style.display = 'none';
                                        } catch {
                                          // ignore
                                        }
                                      }}
                                    />
                                  ) : null}
                                  <span style={{ minWidth: 0 }}>
                                    <span>
                                      {name} x{qty}
                                    </span>
                                    {String(it?.item_notes || it?.itemNotes || '').trim() ? (
                                      <div className="pos-item-notes-highlight">
                                        <strong>Item Notes:</strong> {String(it?.item_notes || it?.itemNotes || '').trim()}
                                      </div>
                                    ) : null}
                                  </span>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : null}

                    {canTakePayment ? (
                      <div className="pos-order-links">
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => takePayment(o)}
                          disabled={busy || loadingMore}
                          style={{ width: '100%', marginTop: 10 }}
                        >
                          Take payment (Square POS)
                        </button>
                        <div className="field-hint" style={{ marginTop: 6 }}>
                          Opens Square POS on this device for card-present or cash.
                        </div>
                      </div>
                    ) : null}

                    {canMarkPaid ? (
                      <div className="pos-order-links">
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => markAsPaid(o)}
                          disabled={busy || loadingMore}
                          style={{ width: '100%', marginTop: 10 }}
                        >
                          Mark as Paid
                        </button>
                        <div className="field-hint" style={{ marginTop: 6 }}>
                          Use only if payment was received outside the online checkout flow.
                        </div>
                      </div>
                    ) : null}

                    {canCancel ? (
                      <div className="pos-order-links">
                        <div className="field-hint" style={{ marginTop: 10 }}>
                          {posProvider === 'SQUARE'
                            ? shouldAutoRefund
                              ? 'Cancels the Square fulfillment (queue) and attempts full refund if paid online.'
                              : 'Cancels the Square fulfillment (queue). Refunds for in-person payments must be done in Square POS.'
                            : `Cancels the order in EzSell. Refunds/cancellations in ${providerLabel} must be handled manually in ${providerLabel}.`}
                        </div>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => cancelOrder(o)}
                          disabled={busy || loadingMore}
                          style={{ width: '100%', marginTop: 10, borderColor: 'rgba(220,38,38,0.35)', color: 'rgba(153,27,27,0.95)' }}
                        >
                          Cancel order
                        </button>
                      </div>
                    ) : (
                      <div className="pos-order-links">
                        <div className="field-hint" style={{ marginTop: 10 }}>
                          {isCancelled ? 'Order is cancelled.' : isPickupComplete ? 'Pickup complete — cancellation disabled.' : ''}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
